/* Generated by uc-options-engine/tools/build-theme-js.js — do not edit.
   Source of truth is uc-options-engine/src/*.js, covered by 72 tests.
   Rebuild after any change there, then push the theme. */
(function () {
  "use strict";

  /* ── engine.js ───────────────────────────────────────────────────── */
  // @ts-check

  /**
   * The options engine — pure, and deliberately so.
   *
   *   resolve({ rules, product, selections }) -> { options, properties, charge, addOns }
   *
   * No DOM, no fetch, no globals. The storefront layer renders what this returns
   * and the parity harness replays the old ruleset through it, which is only
   * possible because it is a function of its arguments.
   *
   * The output that matters most is `properties`: the {name, value} pairs that
   * land on the cart line, travel to ShipStation and drive manufacturing in
   * PourHub. Those must match today byte for byte.
   */

  /* ------------------------------------------------------------------ *
   * Conditions
   * ------------------------------------------------------------------ */

  /**
   * A condition is skipped — never treated as false — when it is incomplete.
   * Seven of them in the live ruleset have no operator at all, left behind in the
   * app's admin. Treating those as false would silently hide working options.
   */
  function isUsableCondition(cond) {
    if (!cond || typeof cond !== "object") return false;
    if (!cond.op || !cond.field) return false;
    return ["equals", "!equals", "contains", "!contains", "present", "!present"].includes(cond.op);
  }

  /**
   * @param {any} cond
   * @param {{product?: any, variant?: any, selections?: Record<string,string>}} ctx
   */
  /**
   * A condition's value may be a COMMA-SEPARATED LIST meaning "any of these" —
   * 58 of the 265 conditions in the live ruleset are written that way, e.g.
   *
   *     Colour Style equals "Fade,Solid,Marble"
   *
   * which reveals Colour 1 for any of those three styles. Comparing the whole
   * string exactly makes that condition unsatisfiable, and the colour pickers —
   * 48 options across the catalogue — simply never appear.
   *
   * Splitting is unambiguous here: no option value in either store contains a
   * comma, so a comma can only ever be a separator.
   */
  function evaluateCondition(cond, ctx) {
    const actual = String(readField(cond, ctx) ?? "");

    /*
     * IS THERE A VALUE AT ALL — asked before the empty-expectation rule below,
     * because these two are the only ops for which an empty `value` is the
     * question rather than a missing answer.
     *
     * `!equals ""` cannot mean this. The rule below reads an empty expectation as
     * "unset, so constrains nothing", which is right for the extracted Live
     * Product Options rules — a blank there meant nobody filled the field in, and
     * hiding an option on it would have hidden 48 colour pickers. So an explicit
     * op is the only way to ask. Uncover Creations targets its ready-mades on the
     * presence of a ready-made code, which is what `emits.json` keys off too.
     */
    if (cond.op === "present") return actual.trim() !== "";
    if (cond.op === "!present") return actual.trim() === "";

    const expected = cond.value == null ? "" : String(cond.value);
    const wanted = expected.split(",").map((v) => v.trim()).filter((v) => v !== "");

    // An empty expectation constrains nothing; treat it as satisfied rather than
    // letting it hide an option.
    if (wanted.length === 0) return cond.op === "equals" || cond.op === "contains";

    switch (cond.op) {
      case "equals":     return wanted.some((w) => eq(actual, w));
      case "!equals":    return !wanted.some((w) => eq(actual, w));
      case "contains":   return wanted.some((w) => contains(actual, w));
      case "!contains":  return !wanted.some((w) => contains(actual, w));
      default:           return true;
    }
  }

  function eq(a, b) {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
  function contains(a, b) {
    return a.toLowerCase().includes(b.toLowerCase());
  }

  function readField(cond, ctx) {
    const { product = {}, variant = {}, selections = {} } = ctx;

    if (cond.source === "options") {
      // Depends on another option's current value, by that option's NAME.
      return selections[cond.field];
    }
    if (cond.source === "variant") {
      if (cond.field === "variant_title" || cond.field === "variant") return variant.title;
      return variant[cond.field];
    }
    // product
    if (cond.field === "product_title") return product.title;
    if (cond.field === "product_tags") return (product.tags || []).join(", ");
    if (cond.field === "product_type") return product.type;
    return product[cond.field];
  }

  /**
   * `mode` is "any" for the app's conditionMode 2, otherwise every usable
   * condition must hold. An option whose conditions are ALL unusable is shown,
   * not hidden.
   */
  function evaluateGroup(group, ctx) {
    if (!group) return true;
    const conditions = (group.conditions || []).filter(isUsableCondition);
    if (conditions.length === 0) return true;

    return group.mode === "any"
      ? conditions.some((c) => evaluateCondition(c, ctx))
      : conditions.every((c) => evaluateCondition(c, ctx));
  }

  /* ------------------------------------------------------------------ *
   * Which option sets apply to a product
   * ------------------------------------------------------------------ */

  function setsForProduct(rules, product, variant) {
    const sets = rules?.sets || [];
    return sets.filter((set) => {
      const applies = set.appliesTo || {};

      // No usable target: renders nowhere. See extract.js targeting().
      if (applies.mode === "none") return false;

      if (applies.mode === "products") {
        const id = String(product?.id ?? "");
        const handle = String(product?.handle ?? "");
        return (
          (applies.productIds || []).some((p) => String(p) === id) ||
          (applies.handles || []).some((h) => h === handle)
        );
      }

      if (applies.mode === "conditions") {
        return evaluateGroup(
          { mode: applies.match === "any" ? "any" : "all", conditions: applies.conditions || [] },
          { product, variant, selections: {} }
        );
      }

      // "all" means every product, and only targetMode 0 ever meant that — which
      // now maps to "none". An unknown mode is a data error, so fail closed.
      return applies.mode === "all";
    });
  }

  /* ------------------------------------------------------------------ *
   * Resolution
   * ------------------------------------------------------------------ */

  const MAX_RESOLVE_PASSES = 12;

  /**
   * @param {{rules:any, product:any, variant?:any, selections?:Record<string,string>}} args
   */
  function resolve({ rules, product, variant = {}, selections = {}, now = Date.now() }) {
    const sets = setsForProduct(rules, product, variant);
    const flat = [];
    for (const set of sets) collect(set.options || [], flat, set.id, now);

    // Visibility and selection settle together: hiding an option removes its
    // value, which can hide the next one. Iterate to a fixed point rather than
    // assuming the author ordered the rules conveniently.
    /*
     * Values can be conditional too, not just options.
     *
     * Two things need it and they are the same shape underneath: an egg style is
     * offered only where a variant exists to make it at this size, and a Herd
     * colourway only on a product tagged for it. Doing this once, before anything
     * else runs, means choosing, pricing, emitting and rendering all see a plain
     * list and none of them has to know why it is short.
     */
    const scoped = flat.map((option) => {
      const values = (option.values || []).flatMap((value) => {
        if (value.visibleWhen && !evaluateGroup(value.visibleWhen, { product, variant, selections: {} })) {
          return [];
        }
        if (!value.bySize) return [value];
        const entry = value.bySize[variant?.title];
        if (!entry) return [];        // nothing to ship at this size, so not offered
        const { bySize, ...rest } = value;
        return [{ ...rest, value: entry.value, addsVariant: entry.addsVariant }];
      });
      /* Always the mapped list, never the original when the count happens to
       * match: at Medium every egg style has a variant, so nothing is dropped —
       * and returning the original there skipped the per-size substitution, which
       * left Medium showing Mini set names and adding nothing to the cart. */
      return { ...option, values };
    });

    let effective = {};
    let visible = new Set();
    let unavailable = new Set();

    for (let pass = 0; pass < MAX_RESOLVE_PASSES; pass++) {
      const nextVisible = new Set();
      const nextUnavailable = new Set();
      const nextEffective = {};

      for (const option of scoped) {
        const context = { product, variant, selections: nextEffective };

        /*
         * THE SIZE RULE IS CHECKED FIRST, AND IT WINS.
         *
         * The cum tube depends on a flat base and is not made in X-Small. At
         * X-Small there is no base to choose, so the dependency can never be
         * satisfied and the question vanished — leaving the customer to work out
         * for themselves that this size cannot have one. But "no cum tube at this
         * size" is the true and useful answer, and it outranks "you have not
         * chosen a base", which at that size is not a thing they can do anything
         * about.
         *
         * So: fail the size rule and the option is shown greyed with its reason,
         * whatever its dependencies say. Pass it, and dependencies decide as
         * normal.
         */
        /* A follow-up to a question that was not asked is not a question. */
        if (!evaluateGroup(option.revealedWhen, context)) continue;

        const available = evaluateGroup(option.availableWhen, context);
        if (!available) {
          nextVisible.add(option.id);
          nextUnavailable.add(option.id);
          continue;
        }

        if (!evaluateGroup(option.visibleWhen, context)) continue;

        nextVisible.add(option.id);

        const chosen = chooseValue(option, selections);
        // First visible definition of a name wins, deterministically. Sinnovator
        // has seven sets that apply to every product, six of which define
        // "Firmness" and rely on option-level conditions to stay mutually
        // exclusive. Where that partition is imperfect we must still emit one
        // value, not six — see `conflicts` below.
        if (chosen !== undefined && nextEffective[option.name] === undefined) {
          nextEffective[option.name] = chosen;
        }
      }

      const settled =
        sameSet(nextVisible, visible) && sameSet(nextUnavailable, unavailable) &&
        sameMap(nextEffective, effective);
      visible = nextVisible;
      unavailable = nextUnavailable;
      effective = nextEffective;
      if (settled) break;
    }

    const shownOptions = scoped.filter((o) => visible.has(o.id));

    const properties = [];
    const addOns = [];
    const charges = [];
    const conflicts = [];
    const emitted = new Set();
    let optionsTotal = 0;

    for (const option of shownOptions) {
      if (unavailable.has(option.id)) continue;
      const selected = effective[option.name];
      if (selected === undefined) continue;

      // A cart line must never carry the same option name twice — PourHub reads
      // these as the manufacturing spec. Later definitions are recorded as
      // conflicts so the rules viewer can show where a ruleset overlaps itself,
      // rather than being swallowed silently.
      if (emitted.has(option.name)) {
        conflicts.push({ name: option.name, ignoredOptionId: option.id });
        continue;
      }
      emitted.add(option.name);

      // Emitted even when empty — the current ruleset does exactly that
      // (e.g. "Also Buy" with a blank value) and parity is the point.
      properties.push({ name: option.name, value: selected });

      const value = findValue(option, selected);
      if (!value) continue;

      // A value either adds a real product OR takes a charge line — never both.
      // In the live ruleset an add-on value carries `price` as well, mirroring
      // the variant's own price (Cum Tube Upgrade: price 15, variant £15.00).
      // Counting both would charge the customer twice for one accessory.
      if (value.addsVariant?.id) {
        addOns.push({
          /* Property names this added product should copy from the parent line —
           * see `inherits` in the registry. */
          inherits: option.inherits,
          variantId: String(value.addsVariant.id),
          quantity: Math.max(1, Number(value.addsVariant.quantity) || 1),
          handle: value.addsVariant.handle,
          title: value.addsVariant.title,
          fromOption: option.name,
        });
      } else if (typeof value.price === "number" && Number.isFinite(value.price) && value.price > 0) {
        // One charge line per priced option, not one per line. Keeps the charge
        // variants to the set of distinct option prices rather than every
        // possible total, which is combinatorial and would fail on an unseen sum.
        charges.push({ option: option.name, value: selected, amount: value.price });
        optionsTotal += value.price;
      }
    }

    return {
      options: shownOptions.map((option) => {
        const off = unavailable.has(option.id);
        /*
         * AN UNAVAILABLE OPTION STILL HAS AN OUTCOME.
         *
         * At X-Small the customer has no CHOICE about the base, but the toy still
         * has one — a flat one — and the base is still not coloured. Greying every
         * value said "none of this applies", which is not what is true and left
         * the row looking broken rather than settled.
         *
         * So the outcome the product actually has is shown as chosen, and only
         * the alternatives are greyed with the reason.
         */
        const fixed = off ? fixedOutcome(option) : undefined;
        return {
          id: option.id,
          name: option.name,
          label: option.label,
          type: option.type,
          /* An explicit control override. Dropping it here silently turned the
           * egg picker back into an inline row — the third time a field added at
           * one end of the pipeline was lost at a hand-copied boundary in the
           * middle. `engine.test.js` now asserts this list carries them. */
          control: option.control,
          isRegion: option.isRegion,
          /* Resolved and emitted, never drawn — see compile.js. Dropped here it
           * renders as a question with one answer, which is the fourth field to
           * be lost at this boundary. */
          hidden: option.hidden,
          /* Read by the storefront's stock pass. The fifth field, same boundary. */
          hideWhenSoldOut: option.hideWhenSoldOut,
          required: !!option.required && !off,
          selected: off ? fixed : effective[option.name],
          /* The alternatives greyed, with the reason beside them. The renderer
           * does not have to know why — it just draws what it is given. */
          values: off
            ? (option.values || []).map((v) => (v.value === fixed ? v : {
                ...v, disabled: true,
                disabledReason: v.disabledReason || option.unavailableReason,
              }))
            : (option.values || []),
          params: off
            ? { ...(option.params || {}), disabledReason: option.unavailableReason }
            : (option.params || {}),
          unavailable: off || undefined,
        };
      }),
      properties,
      addOns,
      charges,
      conflicts,
      charge: { amount: round2(optionsTotal) },
      /*
       * The offer running on this product, from whichever set carried it.
       *
       * Carried on the RESOLUTION rather than looked up by the renderer, because
       * the renderer is handed a resolution and never sees the sets — and because
       * a promotion splits its products into a set of their own, so the set that
       * discounted the prices is exactly the one that describes the discount.
       *
       * First wins: a product in two promoted sets at once is refused by the
       * registry, so there is nothing here to choose between.
       */
      promotion: sets.find((set) => set.promotion)?.promotion,
      missingRequired: shownOptions
        .filter((o) => o.required && !effective[o.name] && !unavailable.has(o.id))
        .map((o) => o.name),
    };
  }

  /** Values that carry a real price but no variant are paid via the charge line. */
  function chooseValue(option, selections) {
    /*
     * A GREYED VALUE IS NEVER THE ANSWER, however it got here.
     *
     * The storefront will not let one be clicked, but a selection also arrives
     * from a saved cart, a shared URL, and a preset applied before the current
     * variant was known — and the strap was still defaulting to Leather on the
     * day Leather went out of stock. A value nobody can choose must not be able
     * to answer the question on their behalf.
     */
    const choosable = (value) => value && !value.disabled;

    const supplied = selections[option.name];
    if (supplied !== undefined && choosable(findValue(option, supplied))) return supplied;

    if (option.default !== undefined && option.default !== ""
        && choosable(findValue(option, option.default))) {
      return option.default;
    }
    /* A default may name a KEY rather than a value, which is the only way to
     * default a size-dependent option: "Alien Eggs Mini Set of 3" is not the same
     * string at Medium, but `alien` is the same style at both. */
    /* Guarded on the default actually being set: without it, `v.key === undefined`
     * matches the first value that has no key at all, which quietly answered every
     * option that was waiting to be asked. */
    if (option.default) {
      const byKey = (option.values || []).find((v) => v.key && v.key === option.default && !v.disabled);
      if (byKey) return byKey.value;
    }
    // Only force a value when the option must have one; otherwise leave it unset
    // so the storefront can ask rather than silently choosing for the customer.
    if (option.required && (option.values || []).length) {
      const first = option.values.find((v) => !v.disabled);
      if (first) return first.value;
    }
    return undefined;
  }

  /**
   * What an option settles on when the customer cannot choose.
   *
   * The declared default, which is the outcome the product has anyway: no base
   * colour, no cum tube, a flat base. Only ever a FREE value — showing a priced
   * one as chosen while charging nothing for it would misstate the price, and
   * charging for something nobody chose would be worse. If the default costs
   * money, nothing is marked chosen and every value greys as before.
   */
  function fixedOutcome(option) {
    const values = option.values || [];
    const declared = option.default
      ? values.find((v) => v.value === option.default || (v.key && v.key === option.default))
      : values[0];
    if (!declared) return undefined;
    const free = !declared.addsVariant?.id &&
      !(typeof declared.price === "number" && declared.price > 0);
    return free ? declared.value : undefined;
  }

  function findValue(option, value) {
    return (option.values || []).find((v) => v.value === value);
  }

  function collect(options, out, setId, now = Date.now()) {
    for (const current of options) {
      /* An offer that has ended: the option as it was before it (compile.js). */
      const option = current.promoUntil && now >= current.promoUntil && current.afterPromo
        ? current.afterPromo : current;
      // Excluded options stay in the data so the decision is visible and
      // reversible, but they never reach a customer or a cart line.
      if (option.excluded) continue;
      if (option.values || option.children) {
        out.push({ ...option, id: option.id || `${setId}:${option.name}` });
      }
      if (option.children) collect(option.children, out, setId, now);
    }
  }

  function sameSet(a, b) {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
  }
  function sameMap(a, b) {
    const ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => a[k] === b[k]);
  }
  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  /* ------------------------------------------------------------------ *
   * Cart lines
   * ------------------------------------------------------------------ */

  /**
   * Turns a resolution into the lines to add. The pairing token is what lets the
   * Cart Transform function merge the charge back onto its parent at checkout;
   * underscore-prefixed properties are hidden in cart and checkout by Shopify and
   * do not reach ShipStation, so they cost nothing downstream.
   */
  function cartLinesFor({ resolution, variantId, quantity = 1, chargeVariantFor, token, productTitle, formProperties = {} }) {
    const group = token || newToken();
    const lines = [];

    lines.push({
      id: String(variantId),
      quantity,
      /*
       * The form's own properties first, then ours.
       *
       * A product page may carry line-item properties that are nothing to do with
       * the options — a ready-made's stock code and firmness, a gift card's
       * recipient. Taking over the submit dropped every one of them until #40359
       * booked a shelf item as one to manufacture.
       *
       * Ours win a clash, because an answer the customer gave beats a default the
       * page was carrying.
       */
      properties: {
        ...formProperties,
        ...toPropertyMap(resolution.properties, pairingProperties(group, "product")),
      },
    });

    /*
     * ONE charge line per product, at the total — not one per option.
     *
     * Possible only because every option price is a multiple of 5 and the dearest
     * single product tops out at 100, so 5-100 in fives covers every combination
     * that can occur. Per-option would need six variants but scatters several
     * charge lines through a checkout we cannot theme; per-product needs twenty
     * and reads as one line under its parent.
     *
     * The labels are deliberately currency-free. A hardcoded "£10" would be wrong
     * for a customer paying in euros, and the price column beside the line is
     * already localised by Shopify.
     */
    const total = resolution.charge.amount;
    if (total > 0) {
      const chargeVariant = chargeVariantFor?.(total);
      if (!chargeVariant) {
        // Never silently under-charge. A missing variant is an ops gap to fix,
        // not something to round away.
        throw new Error(`No charge product configured for ${total}`);
      }
      const contributing = resolution.charges.map((c) => c.option);
      lines.push({
        id: String(chargeVariant),
        // Two of the product means twice the customisation.
        quantity,
        properties: pairingProperties(group, "charge", {
          // Visible at checkout and on the receipt: which product this belongs to
          // and what it paid for. Safe downstream — the charge line carries no
          // SKU, so PourHub skips it, and it never reaches ShipStation at all.
          ...(productTitle ? { For: productTitle } : {}),
          ...(contributing.length ? { Options: contributing.join(", ") } : {}),
        }),
      });
    }

    for (const addOn of resolution.addOns) {
      lines.push({
        id: addOn.variantId,
        quantity: addOn.quantity * quantity,
        properties: pairingProperties(group, "addon", {
          _uc_from: addOn.fromOption,
          // Per-unit multiple, so reconcile can rescale when the parent
          // quantity changes. A set of 3 eggs against 2 products is 6.
          _uc_per: String(addOn.quantity),
          /*
           * Inherited from the parent line, and VISIBLE rather than underscored:
           * the eggs are poured in the same silicone as the ovipositor, to the
           * firmness the customer chose for it, and their own product has only
           * sizes to record. The line used to reach manufacturing with no
           * firmness at all.
           *
           * PourHub reads it here exactly as it reads it on the parent, because
           * it matches on the property NAME wherever it appears.
           */
          ...inheritedProperties(addOn, resolution),
        }),
      });
    }

    return { token: group, lines };
  }

  /** The parent line's values for the properties this add-on inherits. */
  function inheritedProperties(addOn, resolution) {
    const wanted = addOn.inherits || [];
    if (!wanted.length) return {};
    const out = {};
    for (const { name, value } of resolution.properties) {
      if (wanted.includes(name) && value) out[name] = value;
    }
    return out;
  }

  function toPropertyMap(properties, extra) {
    const map = {};
    for (const { name, value } of properties) map[name] = value;
    return { ...map, ...extra };
  }

  /**
   * Properties every line in a configured group carries.
   *
   * `_uc_group` / `_uc_role` are ours: the Cart Transform function pairs lines by
   * them, and they are hidden in cart and checkout and never reach ShipStation.
   *
   * `_cl_options_id` is a deliberate compatibility shim. UncoverInsights'
   * `mv_product_line_facts` attributes a charge to its parent by reading a
   * property of exactly that name, and teaching it `_uc_group` means dropping and
   * rebuilding a matview with 111k rows and eleven dependent objects. Writing the
   * name it already knows costs nothing and keeps the reporting split intact from
   * the first migrated order.
   *
   * No collision with Live Product Options while both run: their tokens look like
   * `clduc5y6yirb`, ours like `ucg_8f3a21c4`, and attribution groups by value.
   *
   * Remove this when that matview is next rebuilt for other reasons, and add
   * `_uc_group` to its predicate then.
   */
  function pairingProperties(group, role, extra = {}) {
    return { _uc_group: group, _uc_role: role, _cl_options_id: group, ...extra };
  }

  /**
   * Build the `chargeVariantFor` lookup from config/charge-variants.json.
   *
   * Amounts are keyed by their canonical number string so 5, "5" and "5.00" all
   * resolve to the same variant — Shopify writes prices as "5.00" while the rules
   * carry 5, and a mismatch there would look exactly like a missing variant.
   */
  function makeChargeLookup(storeConfig) {
    const table = new Map();
    for (const [amount, variantId] of Object.entries(storeConfig?.variants || {})) {
      const n = Number(amount);
      if (!Number.isFinite(n)) continue;
      table.set(String(n), String(variantId));
    }
    return (amount) => table.get(String(Number(amount))) || null;
  }

  /** Amounts the rules can request that the store has no variant for. */
  function missingChargeVariants(rules, storeConfig) {
    const lookup = makeChargeLookup(storeConfig);
    const missing = new Set();
    const walk = (list) => {
      for (const option of list) {
        if (option.excluded) { if (option.children) walk(option.children); continue; }
        for (const value of option.values || []) {
          if (value.addsVariant) continue;
          if (typeof value.price !== "number" || value.price <= 0) continue;
          if (!lookup(value.price)) missing.add(value.price);
        }
        if (option.children) walk(option.children);
      }
    };
    for (const set of rules.sets || []) walk(set.options || []);
    return [...missing].sort((a, b) => a - b);
  }

  function newToken() {
    const r = Math.random().toString(36).slice(2, 10);
    return `ucg_${r}`;
  }

  /* ── control.js ──────────────────────────────────────────────────── */
  // @ts-check

  /**
   * Which control an option renders as.
   *
   * The design brief's first principle: DISPLAY SCALES WITH COUNT, NOT PREFERENCE.
   * Five swatches and forty-five swatches are the same question and want opposite
   * widgets, and that is a property of the value set — so it is derived here
   * rather than authored per option. Nobody picks a widget; the registry says
   * what the values ARE and this decides how they are asked.
   *
   * The thresholds, and what each is for:
   *
   *   yesno     2 values, one of them "nothing"   — Cum Tube, Base Colour, Colour 3
   *   pills     2-5 real alternatives             — Size, Firmness, Base, Colour Style
   *   patterns  6-9 visually distinct values      — Herd coats, Egg styles
   *   drawer    10+ visual values                 — the 37-colour palette
   *   dropdown  10+ values with nothing to see    — internal length in inches
   *
   * The split at 2 is the one that is not about count. Two values that are both
   * real alternatives (Flat Base / Vac-U-Lock) are a "which of these" and get
   * pills like any other. Two values where one is a refusal (No / Yes +£15) is a
   * different kind of decision, and the different shape says so before the words
   * do.
   *
   * The split at 10 is the one that fixes a real defect: four colour questions at
   * 37 swatches put 148 swatches on a product page. A drawer keeps page height
   * constant no matter how big the palette grows, which also means the palette
   * can grow without anyone re-checking the layout.
   */

  /**
   * The one threshold. Up to nine, every value is on screen at once — as pills if
   * there is nothing to look at, as pattern swatches if there is. Above nine the
   * set stops fitting a row, and goes behind a drawer (if the values are worth
   * seeing) or into a select (if they are just a list to read).
   *
   * Nine rather than five: the brief's "3-5 pills" describes Size and Firmness,
   * but Colour Style is six on Uncover Creations and eight on Sinnovator, and a
   * wrapping pill row shows those far better than a dropdown that hides seven of
   * them behind a tap. What actually breaks a pill row is a value too long to
   * read at a glance, and none of these are.
   */
  const ROW_ABOVE = 9;

  /* Kept as separate names because they answer different questions, even where
   * they currently agree: "too many to show" and "too many to lay flat". */
  const DRAWER_ABOVE = ROW_ABOVE;
  const PILLS_ABOVE = ROW_ABOVE;



  /** Value keys that mean "none of this, thanks" rather than naming a choice. */
  const SKIP_KEYS = /^(no|none|skip|off)$/i;

  /** Does this value decline the option rather than answer it? */
  function isSkip(value) {
    if (!value) return false;
    if (value.skip !== undefined) return !!value.skip;
    return SKIP_KEYS.test(value.key || "") || SKIP_KEYS.test(value.value || "");
  }

  /** Can a customer tell these apart by looking? Colours and images can. */
  function isVisual(option) {
    if (option.type === "color-swatch" || option.type === "img-swatch") return true;
    return (option.values || []).some((v) => v.color || v.img);
  }

  /**
   * @param {{type?:string, control?:string, values?:Array<any>, params?:object}} option
   * @returns {"yesno"|"pills"|"patterns"|"drawer"|"dropdown"|"html"}
   */
  function controlFor(option) {
    if (!option) return "pills";

    /* An explicit control is an override, and overriding is legitimate — but it
     * is a decision someone has to make on purpose, which is the point. */
    if (option.control) return option.control;
    if (option.type === "html") return "html";

    const values = option.values || [];

    /* A value that answers OTHER questions is a path, not an option. It gets
     * cards at the top of the page rather than a pill in the middle, because it
     * decides whether the rest get asked at all. Derived from `apply` so a preset
     * cannot be authored as a path without actually applying anything. */
    if (values.some((v) => v.apply && Object.keys(v.apply).length)) return "path";
    const visual = isVisual(option);

    /* A checkbox in the old ruleset was always this question, drawn worse. */
    if (option.type === "checkbox") return "yesno";
    if (values.length === 2 && values.some(isSkip)) return "yesno";

    /* If there is something to look at, show it. A pill can hold a word and a
     * price and nothing else, so drawing a colour or a coat pattern as one throws
     * away the only information the customer is actually choosing on. Small
     * enough to fit — the five strap colours, the four herd coats — means inline;
     * bigger than a row means a drawer. */
    if (visual) return values.length > DRAWER_ABOVE ? "drawer" : "patterns";

    if (values.length > PILLS_ABOVE) return "dropdown";
    return "pills";
  }

  /**
   * Which of the two values is the "yes", and which the refusal.
   *
   * PourHub drops any option whose value is "no", but the property still has to
   * be SENT as "No" — an unchecked box that emits nothing and one that emits "No"
   * look identical on the storefront and different in the warehouse.
   */
  function yesNoValues(option) {
    const values = option.values || [];
    const no = values.find(isSkip);
    const yes = values.find((v) => !isSkip(v)) || values[0];
    return { yes, no };
  }

  /* ── renderer-colour.js ──────────────────────────────────────────── */
  // @ts-check

  /**
   * The colour picker: slots on the page, palette in a drawer.
   *
   * A fully configured dildo asks four colour questions against a 37-swatch
   * palette. Rendered flat that is 148 swatches, and the page grows every time
   * the customer answers something — which was the single worst thing about the
   * app being replaced.
   *
   * The shape that fixes it, from the design brief:
   *
   *   ON THE PAGE, each colour question is ONE ROW — the chosen colour, what it
   *   paints, and a Change control. Four questions is four rows whatever the
   *   palette does, so page height stops depending on catalogue size.
   *
   *   THE PALETTE LIVES IN A DRAWER, chunked by hue. It opens over the page, so
   *   the 37 swatches cost no layout at all until asked for.
   *
   * ONE DRAWER PER PALETTE, NOT PER QUESTION. Main, second, third and base
   * colour all pick from the same 37, so they share one drawer and the mount
   * layer records which slot it is currently filling. Four copies of the same
   * palette in the DOM would be four times the markup to say one thing, and the
   * strap's five colours are a genuinely different list — which is why the
   * grouping is by palette rather than a single global drawer.
   *
   * Nothing here hides an answer. A collapsed question still shows its answer;
   * the drawer is a way to change one, not a place answers go to live.
   */

  /** A colour whose value is not yet chosen still needs something in the well. */
  const EMPTY_CHIP = "linear-gradient(135deg,#eee 45%,#ddd 55%)";

  /**
   * The slots on the page, in the order they were asked.
   *
   * @param {object} opt        the compiled colour option
   * @param {object} helpers    esc/slug from the renderer
   */
  function slotRow(opt, { esc, slug, imageFor }) {
    const chosen = (opt.values || []).find((v) => v.value === opt.selected);
    const id = `uc-opt-${slug(opt.name)}`;
    const paletteId = paletteKey(opt);

    return (
      `<div class="uc-slot" id="${id}-group">` +
        (chosen && (imageFor ? imageFor(chosen) : chosen.img)
          ? `<img class="uc-slot__chip" src="${esc(imageFor ? imageFor(chosen) : chosen.img)}" alt="" loading="lazy">`
          : `<span class="uc-slot__chip" style="background:${esc(chosen?.color || EMPTY_CHIP)}"></span>`) +
        `<span class="uc-slot__text">` +
          `<span class="uc-slot__what">${esc(opt.label || opt.name)}</span>` +
          `<span class="uc-slot__chosen">${esc(chosen ? (chosen.text || chosen.value) : "Not chosen yet")}</span>` +
        `</span>` +
        `<button type="button" class="uc-slot__change"` +
        ` data-uc-open-drawer="${esc(opt.name)}" data-uc-palette="${esc(paletteId)}"` +
        ` aria-haspopup="dialog">${chosen ? "Change" : "Choose"}</button>` +
      `</div>`
    );
  }

  /**
   * Which palette an option draws from. Options compiled from a region carry it
   * explicitly; anything else is grouped by the colours it actually offers, so
   * two lists that happen to be identical still share one drawer.
   */
  function paletteKey(opt) {
    if (opt.params?.palette) return opt.params.palette;
    return `set-${(opt.values || []).map((v) => v.value).join("|").length}`;
  }

  /**
   * One drawer per palette, emitted once after every option.
   *
   * @param {Array<object>} options   every option in the set
   * @param {object} helpers
   * @param {string|null} openSlot    the option name the drawer is currently filling
   */
  function drawers(options, { esc, slug, priceFor, priceTag, imageFor }, openSlot = null) {
    const byPalette = new Map();
    for (const opt of options) {
      if (opt.control !== "drawer") continue;
      const key = paletteKey(opt);
      if (!byPalette.has(key)) byPalette.set(key, opt);
    }
    if (!byPalette.size) return "";

    const openOpt = options.find((o) => o.name === openSlot && o.control === "drawer");
    const openKey = openOpt ? paletteKey(openOpt) : null;

    /*
     * "Choose main colour", but not "Choose choose your squishy".
     *
     * The heading is built from the question's own label, and this drawer stopped
     * being only a colour picker the moment sixteen squishy styles arrived — one
     * of which is a question whose label already opens with the verb.
     */
    const heading = (opt) => {
      const label = opt.label || "colour";
      return /^choose\b/i.test(label) ? label : `Choose ${label.toLowerCase()}`;
    };

    return [...byPalette.entries()].map(([key, opt]) => {
      const isOpen = key === openKey;
      return (
        `<div class="uc-picker${isOpen ? " is-open" : ""}" data-uc-drawer="${esc(key)}"` +
        ` role="dialog" aria-modal="true" aria-label="${esc(heading(openOpt || opt))}"${isOpen ? "" : " hidden"}>` +
          `<div class="uc-picker__panel">` +
            `<div class="uc-picker__head">` +
              `<h2 class="uc-picker__title">${esc(heading(openOpt || opt))}</h2>` +
              `<button type="button" class="uc-picker__close" data-uc-close-drawer>Close</button>` +
            `</div>` +
            hueChips(opt, { esc }) +
            drawerGrid(openOpt || opt, { esc, slug, priceFor, priceTag, imageFor }) +
          `</div>` +
        `</div>`
      );
    }).join("");
  }

  /**
   * The face of a value: a photograph where there is one, a colour otherwise.
   *
   * This drawer was written for the 37-colour palette and only ever drew a colour
   * chip. The egg styles came later, carrying images and no colour, so they
   * rendered as five identical grey squares — a picker for five things that
   * looked the same.
   */
  function face(value, { esc, imageFor }) {
    const src = imageFor ? imageFor(value) : value.img;
    /*
     * A COLOUR PHOTOGRAPH IS CROPPED; A PRODUCT PHOTOGRAPH IS CONTAINED.
     *
     * `.uc-swatch__img` is `object-fit: contain` so an egg set is not trimmed to
     * a square. Let a colour take that and it is letterboxed on the surface
     * colour — a swatch framed in a colour that is not the one being chosen,
     * which is the complaint this whole change answers, reintroduced.
     *
     * A value carrying `color` is a colour however it is illustrated, so that is
     * the test rather than where the picture came from.
     */
    const isColour = !!value.color;
    return src
      ? `<img class="uc-swatch__img${isColour ? " uc-swatch__img--colour" : ""}"` +
        ` src="${esc(src)}" alt="" loading="lazy">`
      : `<span class="uc-swatch__chip" style="background:${esc(value.color || "#ccc")}"></span>`;
  }

  /** The swatches themselves. Selection is marked on the frame, never by tinting. */
  function drawerGrid(opt, { esc, slug, priceFor, priceTag, imageFor }) {
    const id = `uc-opt-${slug(opt.name)}`;
    const items = (opt.values || []).map((value) => {
      const selected = value.value === opt.selected;
      /* `priceTag` renders the offer's strike-through where there is one; the
       * bare `priceFor` remains the fallback for a caller that has no tag. */
      const tag = priceTag
        ? priceTag(value, opt, "uc-swatch__price")
        : (priceFor && priceFor(value, opt)
            ? `<span class="uc-swatch__price">${esc(priceFor(value, opt))}</span>` : "");
      return (
        `<button type="button" role="radio" aria-checked="${selected}"` +
        ` class="uc-swatch uc-swatch--color${selected ? " is-selected" : ""}"` +
        ` tabindex="${selected ? 0 : -1}"` +
        (value.disabled ? ` disabled` : "") +
        (value.hue ? ` data-uc-hue="${esc(value.hue)}"` : "") +
        facets(value) +
        (value.disabled && value.disabledReason ? ` title="${esc(value.disabledReason)}"` : "") +
        ` data-uc-value="${esc(value.value)}">` +
          face(value, { esc, imageFor }) +
          `<span class="uc-swatch__name">${esc(value.text || value.value)}</span>` +
          tag +
        `</button>`
      );
    });

    return `<div class="uc-swatches uc-swatches--drawer" role="radiogroup"` +
           ` id="${id}-drawer" aria-label="${esc(opt.label || "Colours")}">${items.join("")}</div>`;
  }

  /**
   * Filter chips over the hue groups. Purely a view over the same swatches — the
   * value set never changes, so a filter can never hide a colour the customer
   * could have had. `skin` is the group the extenders lean on, which is why Skin
   * Tone did not need to become a colour style of its own.
   */
  function hueChips(opt, { esc }) {
    const hues = opt.params?.hues || [];
    if (hues.length < 2) return "";
    /*
     * `data-uc-hue-FILTER`, not `data-uc-hue`. The swatches themselves carry
     * `data-uc-hue` so the filter can find them, and they are buttons too — so a
     * handler that matched `[data-uc-hue]` swallowed every colour click and
     * filtered instead of selecting. Nobody could pick a colour at all.
     */
    return (
      `<div class="uc-hues" role="group" aria-label="Filter colours">` +
        `<button type="button" class="uc-hue is-active" data-uc-hue-filter="all">All</button>` +
        hues.map((hue) =>
          `<button type="button" class="uc-hue" data-uc-hue-filter="${esc(hue)}">${esc(HUE_LABELS[hue] || hue)}</button>`
        ).join("") +
      `</div>`
    );
  }

  /**
   * What a chip can match this swatch on: its hue, and `glow` when the colour is
   * one of the glow-in-the-dark pours.
   *
   * A LIST rather than one attribute per facet, because a hue and a glow are the
   * same kind of thing to the filter — it asks "is this swatch in that group",
   * and the answer should not depend on which group was asked about.
   */
  function facets(value) {
    const list = [value.hue, value.glow ? "glow" : null].filter(Boolean);
    return list.length ? ` data-uc-facets="${list.join(" ")}"` : "";
  }

  const HUE_LABELS = {
    neutral: "Neutrals",
    pink: "Pinks",
    purple: "Purples",
    blue: "Blues",
    green: "Greens",
    gold: "Golds",
    glow: "Glow in the dark",
    red: "Reds",
    skin: "Skin tones",
  };

  /* ── renderer.js ─────────────────────────────────────────────────── */
  // @ts-check

  /**
   * Turns a resolved option set into HTML.
   *
   * Kept as string generation with no DOM access so it can be tested in node,
   * the same way the shipping selector and the gift engine were. The mount layer
   * (storefront.js) owns events and re-rendering; this owns markup only.
   *
   * PRICES ARE NOT FORMATTED FROM THE RULE AMOUNT. The rules carry GBP figures,
   * but a US customer pays whatever the charge variant costs in their market —
   * which only Shopify knows. Liquid emits those localised prices and the
   * renderer looks them up, so the number beside an option is always the number
   * that will be charged. Displaying a converted rule amount is exactly the bug
   * this whole project exists to remove.
   *
   * WHICH CONTROL an option draws as is not decided here and not authored in the
   * registry — `control.js` derives it from the value set. This file knows how to
   * draw each control, not which one a question deserves.
   */


  const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

  /** Everything interpolated is merchant-authored config, not customer input — escaped anyway. */
  function esc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => ESCAPES[c]);
  }

  /**
   * @param {object} deps
   * @param {(amount:number)=>string|null} deps.priceLabel  localised, e.g. "+$10.00"
   */
  function createRenderer({
    /*
     * WORDING THE RENDERER SUPPLIES ITSELF, from registry/copy.json via the
     * bundle, which is built per store. These were literals here, and this file
     * is one file on both shops — so a tone-of-voice pass written for one of them
     * rewrote the other's storefront too. Each keeps its original English as the
     * fallback, so a key removed from the registry degrades rather than blanks.
     */
    copy = {},
    priceLabel = () => null,
    addonPriceLabel = () => null,
    addonImage = () => null,
    colourImage = () => null,
    openSlot = null,
    variantTitle = null,
    productTitle = null,
    productImage = null,
  } = {}) {
    /*
     * COPY THAT HAS TO KNOW WHERE IT IS.
     *
     * Two things the copy genuinely needs and a fixed string cannot do:
     *
     *   {{product}}          the product's own name, so the wand's "No" can say
     *                        what it is you would be missing out on
     *   {{if:Base}}…{{end}}  a clause that only makes sense where that question
     *                        is asked. "Most popular" promises a Flat Base, and
     *                        grinders have no base at all — so on a grinder the
     *                        card was promising something that does not exist.
     *
     * Deliberately not a template language: two forms, both of which fail to
     * plain text rather than to an error, because this is merchant copy and a
     * typo in it must not take the page down.
     */
    const interpolate = (text) => {
      if (!text || !/\{\{/.test(text)) return text;
      return String(text)
        .replace(/\{\{\s*if:([^}]+?)\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g,
          (_, name, body) => (present.has(name.trim()) ? body : ""))
        .replace(/\{\{\s*product(?:_name)?\s*\}\}/g, productTitle || "this")
        .replace(/\s+([.,;:!?])/g, "$1")
        .trim();
    };

    /* The option names on screen right now, for {{if:…}}. */
    let present = new Set();

    /**
     * What a value costs, in the customer's currency.
     *
     * Two different sources, and mixing them up is the bug this project exists to
     * remove. A charge value is priced by the charge PRODUCT for that amount; a
     * value that adds a real product is priced by THAT product's variant. Neither
     * is ever the GBP figure in the rules — showing £19 to a customer about to be
     * charged $24 is exactly the failure being replaced, so an unknown price
     * shows nothing at all.
     */
    const priceFor = (value, opt) => {
      if (!value) return null;
      if (value.addsVariant?.id) return addonPriceLabel(value.addsVariant.id);
      /*
       * "Add eggs? Yes +£29" — where the £29 is the egg set's own price, read from
       * the option that will actually add it. Writing the number on the Yes would
       * be a copy that goes stale the day egg sets are repriced, and the customer
       * would be told one figure and charged another.
       */
      if (value.priceFromVariant) return addonPriceLabel(value.priceFromVariant);
      /* Against the configuration WITHOUT this option's current answer, so the
       * number is what choosing it actually adds. See `priceLabel`. */
      return value.price ? priceLabel(value.price, baselineFor(opt)) : null;
    };

    /**
     * The price element for a value, struck through when an offer took it off.
     *
     * ONE HELPER FOR FIVE CONTROLS. Paths, pills, yes/no, patterns and swatches
     * each draw their own price span, and a promotion that only some of them knew
     * about would be a page where firmness says Free and the path it is chosen
     * through still says £10.
     *
     * `wasPrice` is priced through the SAME delta as any other figure, so the
     * struck number is the local one — a US customer sees the dollars they are
     * not paying, not the pounds.
     */
    const priceTag = (value, opt, cls) => {
      /* Free because the cart adds it as a gift — no figure to strike. */
      if (value?.gift) {
        return `<span class="${cls} ${cls}--was"><b class="uc-free">${esc(copy.promotionFree || "Free")}</b></span>`;
      }
      const was = value?.wasPrice ? priceLabel(value.wasPrice, baselineFor(opt)) : null;
      if (was) {
        return `<span class="${cls} ${cls}--was">` +
                 `<s class="uc-was">${esc(was)}</s> ` +
                 `<b class="uc-free">${esc(copy.promotionFree || "Free")}</b>` +
               `</span>`;
      }
      const price = priceFor(value, opt);
      return price ? `<span class="${cls}">${esc(price)}</span>` : "";
    };

    /* The charge total this render is showing, and what each option contributes
     * to it. Set once per render, like `siblings`. */
    let chargeTotal = 0;
    let chargeByOption = new Map();

    /** The total with this option's own contribution taken out. */
    const baselineFor = (opt, names) => {
      const remove = names || (opt ? [opt.name] : []);
      let total = chargeTotal;
      for (const name of remove) total -= chargeByOption.get(name) || 0;
      return Math.max(0, total);
    };

    /* Every option in the current resolution. A path prices itself from the
     * values it applies, which live on its siblings. Set once per render. */
    let siblings = [];

    /**
     * The picture for a value.
     *
     * A value that adds a product is illustrated by THAT PRODUCT's own image,
     * handed over by Liquid, so the eggs show whatever the egg listing shows and
     * a re-shoot reaches the options without anyone editing a URL. The registry's
     * own `img` is the fallback for values that add nothing — and for the case
     * where the product has no image at all.
     */
    const imageFor = (value) => {
      /*
       * A COLOUR IS NEVER A PRODUCT PHOTOGRAPH — but it can be a photograph OF
       * THE COLOUR, and that is a different thing.
       *
       * The five strap colours each add a product — the same product — so pricing
       * them by their variant is right and illustrating them by it is not: all
       * five rendered the one photograph of a bundle of straps, which is a colour
       * picker where every colour looks identical. That is what this guard is
       * for, and it still holds: a colour never takes a linked product's picture.
       *
       * What it may take is its OWN shot from the colour guide. A hex cannot show
       * a marble, a glow, or the depth of a skin tone, which is why customers
       * reported the swatches not matching what arrived. `image` on the palette
       * entry is that shot and it wins; the hex stays as the fallback, so a
       * palette with no photographs (the strap's five) is unchanged.
       */
      if (value?.color) return colourImage(value) || null;
      /* The colourway that IS the product as photographed — Signature Color, UC
       * Signature. Not a pick, so the picture is this listing's own, and it
       * follows a re-shoot with nothing to update. */
      if (value?.usesProductImage) return productImage || value?.img || null;
      return (value?.addsVariant?.id ? addonImage(value.addsVariant.id) : null) || value?.img || null;
    };

    /** The step wrapper every question shares, whatever it draws as. */
    function option(opt, index = 0, closesGroup = false) {
      const control = controlFor(opt);
      const body = bodyFor(opt, control);
      if (body === null) return "";

      const id = `uc-opt-${slug(opt.name)}`;
      /* No number when this option is not part of the numbered flow, and none
       * when one option is rendered on its own — the count only means anything
       * relative to the others. */
      const number = index > 0 ? `<span class="uc-step__num">${index}.</span> ` : "";
      /* Separators divide STEPS. A colour slot or a rider is part of the step
       * above it, and a rule between each of them chopped one decision into four
       * and spread it down the page. */
      /*
       * A sub-question belongs to the step above it, so no rule between them —
       * but the LAST one closes the group, and the rule goes back on. Without it
       * the colour questions and whatever follows ran together as one undivided
       * block, which is the opposite problem to the one removing the rules
       * solved.
       */
      const isSub = index === 0 && control !== "path";
      const sub = isSub ? ` uc-option--sub${closesGroup ? " uc-option--sub-last" : ""}` : "";

      return [
        `<section class="uc-option uc-option--${esc(control)}${sub}" data-uc-option="${esc(opt.name)}"` +
        ` data-uc-control="${esc(control)}">`,
          control === "html" ? "" :
            `<h3 class="uc-option__label" id="${id}-label">${number}${esc(opt.label || opt.name)}` +
            (opt.required ? `<span class="uc-option__required" aria-hidden="true">*</span>` : "") +
            `</h3>`,
          dependencyLine(opt),
          body,
          helpFor(opt),
        `</section>`,
      ].join("");
    }

    /**
     * "Since you went with Flat Base — want the matching suction cup?"
     *
     * A dependency the customer cannot see reads as a bug: an accessory list that
     * silently changes when you change the base looks broken, and a peripheral
     * label is not enough to fix it. The sentence is static per option because
     * the option itself only exists while its gate holds — the engine's
     * conditions already did the choosing, so nothing has to be recomputed here.
     */
    function dependencyLine(opt) {
      const depends = opt.params?.depends;
      if (!depends) return "";

      /*
       * Two kinds of dependency, one sentence.
       *
       * A fixed one names the answer that revealed this question — the base that
       * chose the accessory. A variant one names the size the customer picked and
       * counts what that leaves, because the egg styles genuinely differ between
       * a Small and a Medium ovipositor and a customer who sees two styles should
       * be told why it is two.
       */
      const subject = depends.on === "variant"
        ? String(depends.subject).replace("{count}", String((opt.values || []).length))
        : depends.subject;
      const cause = depends.on === "variant" ? variantTitle : depends.value;
      if (!cause) return "";
      const sentence = (copy.dependency || "Since you picked {answer} — {sentence}")
        .replace("{answer}", `<b>${esc(cause)}</b>`)
        .replace("{sentence}", esc(subject));
      return `<p class="uc-depends">${sentence}</p>`;
    }

    function bodyFor(opt, control) {
      if (opt.type === "group" || opt.type === "tabs") return null;
      switch (control) {
        case "path":     return pathCards(opt, siblings);
        case "yesno":    return yesNo(opt);
        case "pills":    return pills(opt);
        case "patterns": return patterns(opt);
        case "drawer":   return slotRow(opt, { esc, slug, imageFor });
        case "dropdown": return dropdown(opt);
        case "html":     return opt.params?.html ? `<div class="uc-option__html">${opt.params.html}</div>` : null;
        /* Containers. The engine flattens their children into the same list, so
         * drawing the container itself would draw an empty box around them. */
        case "group":
        case "tabs":     return null;
        default:         return pills(opt);
      }
    }

    /* ── the description box ───────────────────────────────────────────────
     *
     * One component under every pill row, every pattern row and every Yes/No,
     * describing whichever value is currently in play. It is the same box in all
     * three places on purpose: the customer learns where the explanation lives
     * once, instead of once per control.
     *
     * The thumbnail is deliberately SELECTIVE. It belongs on a value that names
     * one fixed, photographable outcome — Signature Color, UC Trademark,
     * Sinnovator Black. On Surprise Me or Marble there is nothing fixed to show,
     * and a picture of one possible result would be a promise we cannot keep.
     */
    function describe(opt, value) {
      if (!value || (!value.description && !imageFor(value) && !value.thumb && !opt.params?.guide)) return "";
      const guide = guideLink(opt, value);
      /*
       * `thumb` marks a value with ONE fixed, photographable outcome and says
       * what the shot is. Until the photograph exists the box is drawn as a
       * labelled placeholder rather than omitted — a missing picture that is
       * visible gets taken; a missing picture that is silent does not.
       */
      const picture = imageFor(value);
      const thumb = picture
        ? `<span class="uc-desc__thumb"><img src="${esc(picture)}" alt="" loading="lazy"></span>`
        : value.thumb
        ? `<span class="uc-desc__thumb uc-desc__thumb--todo" role="img"` +
          ` aria-label="Image needed: ${esc(value.thumb)}" title="Image needed: ${esc(value.thumb)}">` +
          `<span>image</span></span>`
        : "";

      return (
        `<div class="uc-desc${thumb ? " uc-desc--thumb" : ""}" data-uc-desc>` +
          thumb +
          `<span class="uc-desc__body">` +
            /* `title` where the value has one — on a Yes/No the button label is
             * "Yes", and a box headed "Yes" tells the customer nothing about
             * what they would be adding. */
            `<b class="uc-desc__name">${esc(value.title || value.text || value.value)}</b>` +
            (value.description
              ? `<span class="uc-desc__copy">${esc(interpolate(value.description))}</span>` : "") +
            guide +
          `</span>` +
        `</div>`
      );
    }

    /**
     * "Watch our Firmness Guide" — one link per guide, opened in the store's own
     * drawer rather than as a navigation.
     *
     * `uc-open-drawer-btn` + `data-url` + `ucOpenDrawer` is the store's existing
     * convention (custom.js), and it already understands a `#hash` — so the guide
     * still opens scrolled to the value the customer was looking at, without
     * taking them off the product they are configuring. Sending them to a page
     * mid-build loses the configuration.
     *
     * The href is the real URL, not "#", so the link still works if custom.js
     * has not loaded.
     */
    function guideLink(opt, value) {
      const guide = opt.params?.guide;
      if (!guide?.url || !value) return "";
      const href = `${guide.url}#${slug(value.value)}`;
      return (
        `<a class="uc-desc__guide uc-open-drawer-btn" href="${esc(href)}"` +
        ` data-url="${esc(href)}" onclick="ucOpenDrawer(event, this)">` +
          `<span class="uc-desc__play" aria-hidden="true">&#9654;</span> ` +
          `${esc(guide.label || "Read the guide")}</a>`
      );
    }

    function selectedValue(opt) {
      return (opt.values || []).find((v) => v.value === opt.selected);
    }

    /* ── paths ─────────────────────────────────────────────────────────────
     *
     * Cards rather than pills, at the top, because this is not one question
     * among the others — it decides whether the others get asked at all. Each
     * card says in its own copy what it sets, which is why nothing further down
     * repeats the "most popular" claim.
     */
    function pathCards(opt, siblings = []) {
      const items = (opt.values || []).map((value) => {
        const selected = value.value === opt.selected;
        const price = pathPrice(value, siblings);
        return (
          `<button type="button" role="radio" aria-checked="${selected}"` +
          ` class="uc-path${selected ? " is-selected" : ""}"` +
          ` tabindex="${selected ? 0 : -1}" data-uc-value="${esc(value.value)}">` +
            `<span class="uc-path__name">${esc(value.text || value.value)}</span>` +
            /* NOT `priceTag`: a path is priced by `pathPrice` from the values it
             * APPLIES, not from a price of its own, so the tag would find nothing
             * and every path card would silently lose its figure. `pathPrice`
             * returns the offer's pair itself, for the same reason. */
            (price && typeof price === "object"
              ? `<span class="uc-path__price uc-path__price--was">` +
                  `<s class="uc-was">${esc(price.was)}</s> ` +
                  `<b class="uc-free">${esc(price.now)}</b>` +
                `</span>`
              : price ? `<span class="uc-path__price">${esc(price)}</span>` : "") +
            (value.description
              ? `<span class="uc-path__desc">${esc(interpolate(value.description))}</span>` : "") +
          `</button>`
        );
      });
      const id = `uc-opt-${slug(opt.name)}`;
      return `<div class="uc-paths" role="radiogroup" id="${id}-group" aria-labelledby="${id}-label">` +
             `${items.join("")}</div>`;
    }

    /**
     * What a path costs, priced from the values it actually applies.
     *
     * "Most popular" chooses a premium colourway, and choosing it silently added
     * that charge at the bottom of the page with nothing on the card to say so.
     * The figure is read from the applied values rather than written on the path,
     * so changing what the path recommends changes what it says it costs.
     *
     * The charges are SUMMED and labelled once, because that is how they are
     * billed — one charge line at the total, not one per option. That also means
     * the label is looked up on a charge product that genuinely exists, since the
     * engine builds its charge variants from reachable totals.
     */
    function pathPrice(value, siblings) {
      const applied = Object.entries(value.apply || {});
      if (!applied.length) return null;

      let charges = 0;
      /* What the same build carried before an offer took the charges off. A path
       * applies several answers at once, so this is the sum across all of them. */
      let was = 0;
      const answered = [];
      for (const [name, chosen] of applied) {
        answered.push(name);
        const option = siblings.find((o) => o.name === name);
        const target = (option?.values || []).find((v) => v.value === chosen);
        if (!target) continue;
        /*
         * A value that adds a PRODUCT is priced by that product, and its price
         * reaches us already formatted for the customer's market — there is no
         * number to add to the charges. No path applies one today; if one ever
         * does, saying nothing is better than naming a figure that leaves it out.
         */
        if (target.addsVariant?.id) return null;
        if (typeof target.price === "number") charges += target.price;
        was += typeof target.wasPrice === "number" ? target.wasPrice : (target.price || 0);
      }
      /* A path answers several questions at once, so its baseline is the total
       * with ALL of them taken out — the same rule as a single option. */
      const baseline = baselineFor(null, answered);
      /*
       * A PATH IS HOW MOST CUSTOMERS TAKE THE OFFER, so it has to show it.
       *
       * "Most popular" applies Surprise Me Premium, which the Dragon offer makes
       * free — so the card's price fell to nothing and it rendered no price at
       * all, silently. That is indistinguishable from a path that was always
       * free, on the one control that most customers actually press.
       *
       * Priced through the ladder like every other figure, and only when the
       * charges genuinely fell: `was > charges` rather than "is there a
       * wasPrice", so a path mixing a discounted answer with a full-price one
       * still shows its real remaining cost rather than claiming to be free.
       */
      if (was > charges) {
        const wasLabel = priceLabel(was, baseline);
        const nowLabel = charges > 0 ? priceLabel(charges, baseline) : null;
        if (wasLabel) {
          return {
            was: wasLabel,
            now: nowLabel || copy.promotionFree || "Free",
            free: !nowLabel,
          };
        }
      }
      return charges > 0 ? priceLabel(charges, baseline) : null;
    }

    /* ── yes / no ──────────────────────────────────────────────────────────
     *
     * Two bordered buttons, not a pill row and not a checkbox. The shape is the
     * signal: this is "do you want this at all", where the pills above are
     * "which of these". A checkbox also loses the No — and PourHub needs the
     * property sent as "No", not omitted, because an option nobody was offered
     * and one that was declined are different facts.
     */
    function yesNo(opt) {
      const { yes, no } = yesNoValues(opt);
      if (!yes) return null;

      const price = priceFor(yes, opt);
      const chosen = opt.selected;
      const unavailable = yes.disabled;

      const button = (value, label, extra) =>
        `<button type="button" role="radio" aria-checked="${value?.value === chosen}"` +
        ` class="uc-yesno${value?.value === chosen ? " is-selected" : ""}"` +
        (value?.disabled ? " disabled" : "") +
        ` data-uc-value="${esc(value?.value)}">${esc(label)}${extra || ""}</button>`;

      const id = `uc-opt-${slug(opt.name)}`;
      return (
        `<div class="uc-yesno-row" role="radiogroup" id="${id}-group" aria-labelledby="${id}-label">` +
          (no ? button(no, no.text || "No") : "") +
          button(yes, yes.text || "Yes", priceTag(yes, opt, "uc-yesno__price")) +
        `</div>` +
        /* Greyed with the reason beside it, never removed. A control that
         * vanishes reads as a missing feature; one that explains itself reads as
         * a considered product — and the customer stops hunting for it. */
        (unavailable && yes.disabledReason
          ? `<p class="uc-unavailable">${esc(yes.disabledReason)}</p>` : "") +
        /* Always the YES, even while No is selected. The box is what the offer
         * IS — a customer who has not said yes yet is exactly the one who still
         * needs to read it, and swapping it for a description of "No" would say
         * nothing. */
        /* The box is headed by what is being offered. On a Yes/No the yes value
         * is literally called "Yes", which names nothing — so the option's own
         * label stands in, and "Add a third colour" heads the box instead. */
        describe(opt, yes.title ? yes : { ...yes, title: opt.label || opt.name })
      );
    }

    /* ── pills ─────────────────────────────────────────────────────────── */

    function pills(opt) {
      const id = `uc-opt-${slug(opt.name)}`;
      const focusValue = focusFor(opt);

      const items = (opt.values || []).map((value) => {
        const selected = value.value === opt.selected;
        const price = priceFor(value, opt);
        return (
          `<button type="button" role="radio" aria-checked="${selected}"` +
          ` class="uc-pill${selected ? " is-selected" : ""}"` +
          ` tabindex="${value.value === focusValue ? 0 : -1}"` +
          (value.disabled ? ` disabled` : "") +
          (value.disabled && value.disabledReason ? ` title="${esc(value.disabledReason)}"` : "") +
          ` data-uc-value="${esc(value.value)}">` +
            /* The label carries its own text again in `data-label`, so the CSS can
             * reserve the width it will take when bold and the row does not move
             * when something is chosen. */
            `<span class="uc-pill__name" data-label="${esc(value.text || value.value)}">` +
            `${esc(value.text || value.value)}</span>` +
            priceTag(value, opt, "uc-pill__price") +
            /* One tag per set, and it is checked. Repeating "most popular" on
             * every row dilutes it into decoration instead of a recommendation. */
            (value.tag ? `<span class="uc-pill__tag">${esc(value.tag)}</span>` : "") +
          `</button>`
        );
      });

      return (
        `<div class="uc-pills" role="radiogroup" id="${id}-group" aria-labelledby="${id}-label">` +
        `${items.join("")}</div>` +
        unavailableNote(opt) +
        describe(opt, selectedValue(opt))
      );
    }

    /* ── patterns ──────────────────────────────────────────────────────────
     *
     * Six to nine values that differ by appearance — herd coats, egg styles.
     * Big enough to judge by eye, few enough that a drawer would be ceremony.
     */
    function patterns(opt) {
      const id = `uc-opt-${slug(opt.name)}`;
      const focusValue = focusFor(opt);

      const items = (opt.values || []).map((value) => {
        const selected = value.value === opt.selected;
        const price = priceFor(value, opt);
        const image = imageFor(value);
        const face = image
          ? `<img class="uc-pattern__img" src="${esc(image)}" alt="" loading="lazy">`
          : `<span class="uc-pattern__chip" style="background:${esc(value.color || "#ccc")}"></span>`;
        return (
          `<button type="button" role="radio" aria-checked="${selected}"` +
          ` class="uc-pattern${selected ? " is-selected" : ""}"` +
          ` tabindex="${value.value === focusValue ? 0 : -1}"` +
          (value.disabled ? ` disabled` : "") +
          (value.hue ? ` data-uc-hue="${esc(value.hue)}"` : "") +
          facets(value) +
          (value.disabled && value.disabledReason ? ` title="${esc(value.disabledReason)}"` : "") +
          ` data-uc-value="${esc(value.value)}">` +
            face +
            `<span class="uc-pattern__name">${esc(value.text || value.value)}</span>` +
            priceTag(value, opt, "uc-pattern__price") +
          `</button>`
        );
      });

      return (
        `<div class="uc-patterns" role="radiogroup" id="${id}-group" aria-labelledby="${id}-label">` +
        `${items.join("")}</div>` +
        unavailableNote(opt) +
        describe(opt, selectedValue(opt))
      );
    }

    /** Roving tabindex: with nothing selected the group would have no tab stop. */
    function focusFor(opt) {
      const fallback = (opt.values || []).find((v) => !v.disabled)?.value;
      return opt.selected !== undefined ? opt.selected : fallback;
    }

    function unavailableNote(opt) {
      const disabled = (opt.values || []).filter((v) => v.disabled);
      if (!disabled.length) return "";
      const reason = opt.params?.disabledReason || disabled.find((v) => v.disabledReason)?.disabledReason;
      return reason ? `<p class="uc-unavailable">${esc(reason)}</p>` : "";
    }

    function dropdown(opt) {
      const id = `uc-opt-${slug(opt.name)}`;
      const items = (opt.values || []).map((value) => {
        const price = priceFor(value, opt);
        /* An <option> holds text and nothing else, so the offer is said in words
         * rather than drawn with a strike. */
        const was = value.wasPrice ? priceLabel(value.wasPrice, baselineFor(opt)) : null;
        const note = value.gift ? ` (${copy.promotionFree || "Free"})` : was
          ? ` (${copy.promotionFree || "Free"}, was ${was.replace(/^\+/, "")})`
          : price ? ` (${price})` : "";
        const label = (value.text || value.value) + note;
        return `<option value="${esc(value.value)}"${value.value === opt.selected ? " selected" : ""}` +
               `${value.disabled ? " disabled" : ""}>${esc(label)}</option>`;
      });
      const placeholder = opt.selected === undefined
        ? `<option value="" selected disabled>${esc(opt.params?.placeholder || "Choose…")}</option>` : "";
      return `<select class="uc-select" aria-labelledby="${id}-label" data-uc-input>` +
             `${placeholder}${items.join("")}</select>` + describe(opt, selectedValue(opt));
    }

    /**
     * The explainer that is not about one value — "not sure which firmness?".
     * A disclosure rather than a tooltip, because a tooltip is unreachable on a
     * phone and nobody opens one anyway. A plain string is legacy HTML from the
     * app and passes through as it always did.
     */
    function helpFor(opt) {
      const help = opt.params?.help ?? opt.params?.info;
      if (!help) return "";
      /*
       * A plain string is a NOTE ABOUT THE WHOLE QUESTION rather than about the
       * answer currently selected — the strap's "every colour is free", the
       * orifice colours' one line of instruction. It gets the same box as a
       * value's description, because on the page it is doing the same job and a
       * run of grey boxes with one bare paragraph among them reads as something
       * that failed to render rather than as a different kind of remark.
       *
       * No `uc-desc__name`: a value's box is headed by the value it explains, and
       * this explains the question, which the heading above it has already named.
       *
       * Passed through unescaped, as it always has been — this branch also
       * carries legacy HTML from the Live Product Options export.
       */
      if (typeof help === "string") {
        return `<div class="uc-desc">` +
               `<span class="uc-desc__body uc-desc__copy">${help}</span></div>`;
      }

      const link = help.link
        ? `<a class="uc-help__link" href="${esc(help.link.url)}">${esc(help.link.label)}</a>` : "";
      return (
        `<details class="uc-help">` +
          `<summary class="uc-help__summary">${esc(help.title || "Need help choosing?")}</summary>` +
          (help.body ? `<p class="uc-help__body">${esc(help.body)}</p>` : "") +
          link +
        `</details>`
      );
    }

    /* ── the whole set ─────────────────────────────────────────────────── */

    /**
     * Paths first, then the numbered steps, then the drawers.
     *
     * The step numbers are assigned at render time over the options that are
     * actually on screen, so a product that does not ask about a base does not
     * jump from 3 to 5 — which is the sort of thing that reads as a broken page
     * rather than a shorter one.
     */
    function render(resolution) {
      /*
       * Hidden options are dropped HERE and nowhere else.
       *
       * They are fully resolved and they reach the cart as properties — the
       * bullet hole is on every grinder that takes one, and the studio reads it
       * off the order. The customer simply has nothing to decide, so drawing a
       * question with a single answer would be noise.
       */
      const options = (resolution.options || [])
        .filter((o) => !o.hidden)
        .map((o) => ({ ...o, control: controlFor(o) }));
      /* Both: `siblings` prices a path from the values it applies, `present`
       * decides whether a {{if:…}} clause has anything to refer to. */
      siblings = options;
      present = new Set(options.map((o) => o.name));
      chargeTotal = resolution.charge?.amount || 0;
      chargeByOption = new Map((resolution.charges || []).map((c) => [c.option, c.amount]));
      const paths = options.filter((o) => o.control === "path");

      /*
       * A PATH TAKES OVER THE QUESTIONS IT ANSWERS.
       *
       * Choosing "Most popular" and then still being asked for firmness, colour
       * style and a third colour is the whole point of the path defeated — the
       * customer picked it precisely so as not to answer those. So every option
       * the active path applied comes off the page, and the card's own copy is
       * what says which choices were made.
       *
       * They are only hidden from VIEW. The selections stay in the resolution and
       * on the cart line, because manufacturing still has to be told the firmness
       * whether the customer chose it or accepted it.
       *
       * The add-ons — bullet, suction cup, cum tube — are deliberately not in any
       * path's `apply`, so they keep being offered. Taking a recommended build is
       * not the same as declining the extras.
       */
      const answeredByPath = new Set();
      for (const path of paths) {
        const chosen = (path.values || []).find((v) => v.value === path.selected);
        for (const name of Object.keys(chosen?.apply || {})) answeredByPath.add(name);
      }

      /*
       * Everything the path names, whether or not it could be set. A greyed-out
       * base under "Most popular" is a question the customer is not being asked
       * and cannot act on, so it is noise on the recommended route — the place to
       * discover that X-Small has no base is the custom route, where they are
       * choosing one.
       */
      const steps = options.filter(
        (o) => o.control !== "path" && !answeredByPath.has(o.name));

      /*
       * A colour slot is normally not a step. It exists because a style asked for
       * it, so numbering it turns one decision — "Marble, in these two colours" —
       * into three, and the customer counts more work than they are actually
       * doing.
       *
       * `params.step` overrides that in both directions, because "is this a
       * follow-up?" is a fact about the question and not about its type. The
       * riders carry `false`; the strap carries `true` — it is an ungated region
       * with nothing above it, so left to the default it would be the one
       * question on the page without a number.
       *
       * A drawer is excluded outright either way: it renders as a slot row, and a
       * numbered slot row is the layout this rule exists to prevent.
       */
      const numbered = (o) => {
        if (o.control === "drawer") return false;
        if (o.params?.step !== undefined) return o.params.step !== false;
        return !o.isRegion;
      };

      /*
       * A single step is not a sequence, so it is not numbered.
       *
       * Numbers exist to say how far through the customer is; "1." on its own
       * says there is a second question coming and then does not deliver one.
       * This happens whenever a path answers nearly everything — on a grinder,
       * Most Popular leaves only the bullet.
       */
      const count = steps.filter(numbered).length;

      let n = 0;
      const body = steps.map((o, i) => {
        const isSub = !numbered(o);
        /* Closes the run when the next question starts a new step — or when
         * there is no next question at all. */
        const closes = isSub && !(steps[i + 1] && !numbered(steps[i + 1]));
        return option(o, count > 1 && numbered(o) ? ++n : 0, closes);
      }).join("");

      /*
       * THE CALLOUT GOES FIRST, ABOVE THE PATH.
       *
       * It explains why the prices below are what they are, so it has to be read
       * before them — and the path is the first thing priced. Put after the
       * questions it would be an explanation arriving once the customer had
       * already decided the offer was not for them.
       *
       * It is drawn from the SET, so it cannot appear on a product whose prices
       * were not discounted: both come from the same compiled set, and a set is
       * the unit a promotion splits products into.
       */
      const promo = resolution.set?.promotion || resolution.promotion;
      const callout = promo?.title
        ? `<div class="uc-promo" role="note">` +
            (promo.badge ? `<span class="uc-promo__badge">${esc(promo.badge)}</span>` : "") +
            `<b class="uc-promo__title">${esc(promo.title)}</b>` +
            `<span class="uc-promo__body">${esc(promo.body)}</span>` +
          `</div>`
        : "";

      return (
        callout + paths.map((o) => option(o, 0)).join("") + body +
        drawers(options, { esc, slug, priceFor, priceTag, imageFor }, openSlot)
      );
    }

    /**
     * The running total, immediately above the buy button.
     *
     * It shows the product and the options as separate lines because that is
     * what the basket will contain: Shopify charges the product, then a
     * customisation line, then any product an option added. Presenting one merged
     * figure would be tidier and would misdescribe the receipt — and until the
     * store is on Plus, the checkout genuinely shows those lines apart.
     */
    function total(state) {
      /* Nothing to configure, nothing to total. On a bottle of lube "updates as
       * you build" describes a build that does not exist, and a running total
       * beside a fixed price is just a second price. */
      if (!state || !state.totalMinor) return "";
      if (!(state.resolution?.options || []).length) return "";

      const options = state.optionsTotalMinor;
      const blocked = state.missingRequired || [];
      return (
        `<div class="uc-total" data-uc-total>` +
          `<div class="uc-total__figure">` +
            `<span class="uc-total__amount">${esc(state.totalLabel)}</span>` +
            (options > 0
              ? `<small class="uc-total__note">${
                  esc((copy.totalWithOptions || "includes {amount} of options")
                    .replace("{amount}", state.optionsTotalLabel))}</small>`
              : `<small class="uc-total__note">${
                  esc(copy.totalAtRest || "updates as you build")}</small>`) +
            /*
             * The saving, beside the figure it was taken off.
             *
             * Only when the customer has actually CHOSEN something the offer made
             * free — an offer they have not taken up has saved them nothing, and
             * a number claiming otherwise beside the price they are about to pay
             * is the worst place on the page to be wrong.
             */
            (state.savedLabel
              ? `<small class="uc-total__saved">${
                  esc((state.savedPercentLabel
                        ? (copy.totalSavedPercent || "You save {amount} ({percent})")
                        : (copy.totalSaved || "You save {amount}"))
                    .replace("{amount}", state.savedLabel)
                    .replace("{percent}", state.savedPercentLabel || ""))}</small>`
              : "") +
          `</div>` +
          /*
           * The buy button belongs beside the price the customer just built. The
           * theme's own button is further down past the accordions, so on a long
           * configuration the total was sticky and the way to act on it was not.
           * This submits the theme's form rather than replacing it, so validation,
           * selling plans and the cart drawer all still work.
           */
          /* `is="custom-button"` is the theme's loading button: it wraps its own
           * label, appends a three-dot loader and animates between them whenever
           * aria-busy changes. Reusing it means the sticky bar shows the customer
           * the same "working on it" as the main Add to cart rather than a second
           * bespoke spinner, and without it the bar looked inert for the whole
           * request. boot.js sets the attribute. */
          `<button type="button" is="custom-button" class="uc-total__add" data-uc-add` +
          (blocked.length ? ` disabled aria-describedby="uc-total-blocked"` : "") +
          `>Add to cart</button>` +
          (blocked.length
            ? `<p class="uc-total__blocked" id="uc-total-blocked">${
                esc((copy.blocked || "Choose {questions} first")
                  .replace("{questions}", blocked.join(", ")))}</p>`
            : "") +
        `</div>` +
        /*
         * How the bar knows it is stuck.
         *
         * A zero-height marker immediately after it: while this is in view the
         * bar has reached its resting place at the end of the options and the
         * theme's own buy button is right there, so ours would be the second of
         * two identical buttons. Once it scrolls below the fold the bar is
         * pinned, and ours is the only one on screen.
         *
         * A marker rather than watching the theme's button, because "is our bar
         * stuck" is the question being asked — the answer stays right when the
         * page reflows, when the options grow, and on themes whose buy button we
         * cannot find.
         */
        `<div class="uc-total__sentinel" data-uc-total-sentinel aria-hidden="true"></div>`
      );
    }

    return { render, option, total, esc, describe };
  }

  function slug(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  /* ── cart-sync.js ────────────────────────────────────────────────── */
  // @ts-check

  /**
   * Cart integrity for configured products.
   *
   * Follows the Auto-Gift Engine's shape deliberately (assets/custom.js): an
   * idempotent reconcile that computes what the cart SHOULD contain, diffs it
   * against what it HAS, and applies the difference. `desired === actual` being a
   * no-op is what stops cart:change -> mutate -> cart:change looping, and
   * MAX_RECONCILE_PASSES is the backstop against a rule bug spinning the browser.
   *
   * Three things can go wrong between add-to-cart and checkout, and all three
   * cost real money:
   *
   *   1. The customer removes a configured product. Its charge and add-on lines
   *      survive, and they pay for options on a product they no longer own.
   *   2. The customer raises the quantity to 2. One charge covers two products,
   *      so half the customisation is free.
   *   3. A charge line survives with no parent at all — same as (1) but arrived
   *      at by a different route, e.g. an app or another tab.
   *
   * Reconcile fixes all three by treating the PRODUCT lines as the truth and
   * making every other line in the group agree with them.
   *
   * Pure: the cart API is injected, so the whole thing runs against a simulator
   * in tests rather than needing a storefront.
   */

  const GROUP = "_uc_group";
  const ROLE = "_uc_role";
  const FOR = "_uc_for";
  const FROM = "_uc_from";
  /** UncoverInsights reads this name to attribute a charge to its parent. */
  const INSIGHTS_ID = "_cl_options_id";

  const MAX_RECONCILE_PASSES = 5;

  /**
   * @param {{cart: any, chargeVariantFor?: (amount:number)=>string|null, log?: Function}} deps
   */
  function createCartSync({ cart, log = () => {} }) {

    /** Group the cart's lines by our token. Lines without one are not ours. */
    function groupLines(lines) {
      const groups = new Map();
      for (const line of lines) {
        const token = line.properties?.[GROUP];
        if (!token) continue;
        const role = line.properties?.[ROLE];
        if (!role) continue;

        let group = groups.get(token);
        if (!group) {
          group = { token, products: [], charges: [], addons: [] };
          groups.set(token, group);
        }
        if (role === "product") group.products.push(line);
        else if (role === "charge") group.charges.push(line);
        else if (role === "addon") group.addons.push(line);
      }
      return groups;
    }

    /**
     * What each non-product line's quantity should be, given its parent.
     * Returns a list of {key, quantity} changes; quantity 0 means remove.
     */
    function plan(groups) {
      const changes = [];

      for (const group of groups.values()) {
        const parent = group.products[0];

        // No parent: every remaining line in the group is an orphan. This is the
        // leak that matters most — the customer is being charged for options on
        // a product that is no longer in their cart.
        if (!parent) {
          for (const line of [...group.charges, ...group.addons]) {
            changes.push({ key: line.key, quantity: 0, why: "orphan" });
          }
          continue;
        }

        // Two product lines sharing a token means the token was reused. Touching
        // anything here risks charging the wrong line, so leave the group alone
        // and say so — an untidy cart beats a mispriced one.
        if (group.products.length > 1) {
          log("duplicate token, leaving group alone", group.token);
          continue;
        }

        const parentQty = parent.quantity;
        if (!Number.isInteger(parentQty) || parentQty < 1) continue;

        // One charge line per priced option, each covering one unit of the
        // parent. Two of the product means twice the customisation.
        for (const line of group.charges) {
          if (line.quantity !== parentQty) {
            changes.push({ key: line.key, quantity: parentQty, why: "charge qty" });
          }
        }

        // Add-ons carry their own per-unit multiple in _uc_per, set when added.
        for (const line of group.addons) {
          const per = Number(line.properties?.["_uc_per"]) || 1;
          const want = per * parentQty;
          if (line.quantity !== want) {
            changes.push({ key: line.key, quantity: want, why: "addon qty" });
          }
        }
      }

      return changes;
    }

    /**
     * `mutated` means a change was APPLIED, not merely attempted. The caller
     * repaints on it, and on the cart page repainting is a reload — so a change
     * Shopify keeps refusing must not read as success, or every load would
     * reload again.
     *
     * @returns {Promise<{mutated:boolean, changes:any[]}>}
     */
    async function reconcile(pass = 0) {
      if (pass >= MAX_RECONCILE_PASSES) {
        log("pass cap reached, giving up");
        return { mutated: false, changes: [] };
      }

      const current = await cart.get();
      const changes = plan(groupLines(current.items || []));
      if (changes.length === 0) return { mutated: false, changes: [] };

      let applied = 0;
      for (const change of changes) {
        // Keys are re-read from a fresh cart on every pass, because changing one
        // line can renumber the others.
        const ok = await cart.change({ id: change.key, quantity: change.quantity });
        if (ok) applied += 1;
        else log("could not apply", change);
      }

      const next = await reconcile(pass + 1);
      return { mutated: applied > 0 || next.mutated, changes: changes.concat(next.changes) };
    }

    /**
     * Add a configured product and everything that belongs with it, in ONE
     * request. A partial add — product on, charge missing — is an underpriced
     * order, so it must not be possible for the first call to succeed and the
     * second to fail.
     */
    async function addConfigured({ lines }) {
      if (!lines?.length) throw new Error("nothing to add");
      const ok = await cart.add(lines.map(toCartItem));
      return ok;
    }

    return { reconcile, addConfigured, plan, groupLines };
  }

  function toCartItem(line) {
    return { id: line.id, quantity: line.quantity, properties: line.properties };
  }

  /* ── storefront.js ───────────────────────────────────────────────── */
  // @ts-check

  /**
   * The mount layer: DOM events in, cart lines out.
   *
   * Holds the only mutable state in the system — the customer's selections — and
   * re-resolves from scratch on every change rather than patching. Resolving 181
   * options is microseconds, and recomputing beats maintaining a diff when
   * hiding one option can hide the next.
   *
   * Everything it needs is injected, so it runs against a DOM shim in tests and
   * against a real product page in the theme with the same code.
   */


  /** How often the pinned/at-rest question is re-answered while scrolling, in ms. */
  const STUCK_INTERVAL = 100;

  function mount({
    root,
    rules,
    product,
    variant = {},
    chargePrices = {},        // { "10": 1000 } — minor units, in the CUSTOMER's currency
    addonPrices = {},         // { "<variantId>": 1900 } — the same, for values that add a product
    addonImages = {},         // { "<variantId>": "https://…" } — the product's own picture
    colourImages = {},        // { "Neon Pink": "https://…" } — the colour guide's own shot
    soldOut = [],             // variant ids Liquid found unavailable
    basePrice = 0,            // the variant's own price, minor units
    /* Whole amounts lose their decimals, the same as the theme's own line prices
     * and the same as boot's currency-aware formatter — a fallback that disagreed
     * with the real one would only show up on the pages where it was used. */
    formatMoney = (minor) => (minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2)),
    chargeVariantFor,
    onChange = () => {},
    doc = root?.ownerDocument || globalThis.document,
    win = doc?.defaultView || globalThis.window,
  }) {
    /** @type {Record<string,string>} */
    let selections = {};
    let resolution = null;
    /* The one piece of view state: which colour slot the drawer is currently
     * filling, or null for closed. Everything else on screen is derived from the
     * resolution, which is why re-rendering can be unconditional. */
    let openSlot = null;

    /**
     * A charge's displayed price is the charge variant's price in this market,
     * handed to us by Liquid. Never the rule amount converted — that is the bug
     * this project exists to remove.
     */
    const priceAt = (amount) => (amount === 0 ? 0 : chargePrices[String(amount)]);

    /**
     * WHAT THIS OPTION ADDS, given everything else already chosen.
     *
     * The charge is ONE line at the total, so an option's cost is the difference
     * between the total with it and the total without — not the price of a charge
     * product for its own amount. Those are the same number only while a market's
     * prices are proportional to the GBP ladder, which GBP, AUD and CAD are and
     * EUR and USD are not: at £15 + £15 the per-option reading showed €40 for a
     * configuration that charged €45.
     *
     * A delta needs both ends priced. If either is unknown, say nothing rather
     * than guess — the total then understates, which is visible.
     */
    const priceLabel = (amount, baseline = 0) => {
      const from = priceAt(baseline);
      const to = priceAt(baseline + amount);
      if (from === undefined || to === undefined) return null;
      const minor = to - from;
      if (minor <= 0) return null;                    // costs nothing extra here
      return `+${formatMoney(minor)}`;
    };

    /** A value that adds a product is priced by that product, in this market. */
    const addonPriceLabel = (variantId) => {
      const minor = addonPrices[String(variantId)];
      return minor === undefined ? null : `+${formatMoney(minor)}`;
    };

    const renderer = () =>
      createRenderer({ copy: rules?.copy || {}, priceLabel, addonPriceLabel, openSlot,
                      addonImage: (id) => addonImages[String(id)] || null,
                      /*
                       * Keyed by the value's frozen NAME. `image` on the compiled
                       * value is only a filename — Liquid holds the resolved URL,
                       * because the two stores are two CDNs — so a value with no
                       * entry falls back to its hex, which is what every palette
                       * did before the photographs existed.
                       */
                      colourImage: (value) =>
                        (value?.image && colourImages[value.value]) || null,
                      variantTitle: variant?.title, productTitle: product?.title,
                      productImage: product?.image });

    /*
     * A linked product can sell out, and the engine has no idea — stock is a
     * live fact that only Liquid sees. So it is applied here, after resolving:
     * the value greys with a reason, and a customer who had already chosen it
     * has that choice dropped rather than carried silently to a checkout that
     * would reject it.
     *
     * Absence means available. Liquid emits only what is SOLD OUT, so a lookup
     * that failed cannot take a range off sale — the opposite convention would
     * make one broken snippet look like an empty warehouse.
     */
    const outOfStock = new Set((soldOut || []).map(String));

    const isOut = (value) => !!value.addsVariant?.id && outOfStock.has(String(value.addsVariant.id));

    /*
     * ONE OF A PRIMARY AND ITS STAND-INS, NEVER MORE.
     *
     * A value with `fallbackFor` (the 250ml lube for the 500ml) is offered only
     * while its primary is sold out. Of the group, the first in stock is shown;
     * if none is, the primary is shown so it can grey with its reason. This runs
     * even with nothing sold out — otherwise a page with a failed stock lookup
     * would show two Yes buttons for one question.
     */
    function pickStocked(values) {
      const groupOf = (v) => v.fallbackFor ?? v.value;
      const shown = new Map();
      for (const value of values) {
        const group = groupOf(value);
        const members = values.filter((v) => groupOf(v) === group)
          .sort((a, b) => (a.fallbackFor === undefined ? -1 : 0) - (b.fallbackFor === undefined ? -1 : 0));
        if (!shown.has(group)) shown.set(group, members.find((m) => !isOut(m)) || members[0]);
      }
      return values.filter((v) => shown.get(groupOf(v)) === v);
    }

    function applyStock(current) {
      const dropped = [];
      const hidden = [];
      const options = [];
      for (const option of current.options) {
        const values = pickStocked(option.values || []).map((value) =>
          isOut(value)
            ? { ...value, disabled: true, soldOut: true,
                disabledReason: `${value.title || value.text || value.value} is out of stock.` }
            : value);

        /* Nothing it could add is in stock, and the option says to ask nothing. */
        const adds = values.filter((v) => v.addsVariant?.id);
        if (option.hideWhenSoldOut && adds.length && adds.every((v) => v.soldOut)) {
          hidden.push(option.name);
          if (option.selected !== undefined && adds.some((v) => v.value === option.selected)) {
            dropped.push(option.name);
          }
          continue;
        }

        const chosen = values.find((v) => v.value === option.selected);
        /* A choice that is sold out, or whose value stepped aside for a stand-in. */
        const lost = option.selected !== undefined && (!chosen || chosen.soldOut) &&
          (option.values || []).some((v) => v.value === option.selected);
        if (lost) dropped.push(option.name);
        options.push({ ...option, values, selected: lost ? undefined : option.selected });
      }

      /* A hidden question must not reach the line either — not even its "No". */
      const keep = (name) => !hidden.includes(name);
      return {
        resolution: {
          ...current,
          options,
          properties: (current.properties || []).filter((p) => keep(p.name)),
          /* Only a question whose answers ADD products can hide on stock, so it
           * never carries a charge — properties and add-ons are all it can leave. */
          addOns: (current.addOns || []).filter((a) => keep(a.fromOption)),
        },
        dropped,
      };
    }

    function recompute() {
      let stocked = applyStock(resolve({ rules, product, variant, selections }));

      /*
       * A dropped selection has to be resolved AGAIN, not merely blanked.
       *
       * Marking the value unselected leaves the first resolution's `addOns`,
       * `charges` and `properties` still built from it — so a sold-out suction cup
       * came off the buttons and stayed in the cart lines and in the total. The
       * option list is a view; those three are the order.
       */
      for (const name of stocked.dropped) delete selections[name];
      if (stocked.dropped.length) {
        stocked = applyStock(resolve({ rules, product, variant, selections }));
      }
      resolution = stocked.resolution;

      /*
       * A PATH THAT IS THE DEFAULT STILL HAS TO APPLY ITSELF.
       *
       * `select()` applies a path's values when the customer clicks it, but Most
       * Popular is now the default — nobody clicks it — so on first paint nothing
       * had been applied and every question it was supposed to answer was still
       * on the page, unanswered. Applying here covers both routes, and only
       * fills what the customer has not already decided for themselves.
       */
      if (applyPaths()) resolution = applyStock(resolve({ rules, product, variant, selections })).resolution;

      // Drop selections for options that are no longer visible, so a hidden
      // option cannot keep contributing a value the customer cannot see.
      selections = {};
      for (const option of resolution.options) {
        if (option.selected !== undefined) selections[option.name] = option.selected;
      }
      return resolution;
    }

    /** Seed a selected path's answers. Returns true if anything changed. */
    function applyPaths() {
      let changed = false;
      for (const option of resolution.options) {
        const chosen = (option.values || []).find((v) => v.value === option.selected);
        for (const [name, value] of Object.entries(chosen?.apply || {})) {
          if (selections[name] !== undefined) continue;
          selections[name] = value;
          changed = true;
        }
      }
      return changed;
    }

    /*
     * THE COLOUR MODAL HAS TO LEAVE THE PRODUCT SECTION.
     *
     * `position: fixed` is measured from the viewport only while no ancestor
     * creates a containing block — and a `transform`, `filter` or `perspective`
     * anywhere above does exactly that. Impact's product section has one, so the
     * modal was positioned inside that section instead of the window: off-centre,
     * and trapped in a stacking context beneath the sticky header no matter what
     * z-index it asked for. Raising the number could not have fixed it.
     *
     * So it is moved to <body> after every paint. Being outside `root` means the
     * delegated listeners no longer see it, which is why they are attached to the
     * moved node too and why `owns()` exists.
     */
    let portalled = [];

    function portalDrawers() {
      for (const node of portalled) node.remove();
      if (!doc?.body) { portalled = []; return; }   // nothing to portal into
      portalled = [...root.querySelectorAll(".uc-picker")];
      for (const node of portalled) {
        doc.body.appendChild(node);
        node.addEventListener("click", onClick);
        node.addEventListener("keydown", onKeydown);
      }
    }

    /*
     * IS THE BAR PINNED, OR HAS IT REACHED ITS RESTING PLACE?
     *
     * Measured rather than observed. An IntersectionObserver is the usual tool
     * for this and it is the wrong one here: the sentinel sits directly under a
     * `bottom: 0` sticky element, so it is never more than a pixel out of view
     * and the observer's answer is decided by rounding. It also stops reporting
     * entirely while the tab is occluded, which is exactly when a stale answer
     * survives to the moment the customer looks again.
     *
     * The geometry is unambiguous instead: the sentinel's top IS the bar's
     * natural bottom, so the bar is pinned precisely when that natural position
     * has been pushed below the fold.
     */
    let stuckTimer = null;
    let stuckCheckedAt = 0;

    function updateStuck() {
      stuckTimer = null;
      stuckCheckedAt = Date.now();
      const sentinel = root.querySelector("[data-uc-total-sentinel]");
      const bar = root.querySelector("[data-uc-total]");
      if (!sentinel || !bar) return;

      /*
       * How much of the bottom of the screen the bar is covering.
       *
       * `scrollIntoView` honours `scroll-margin`, so publishing the measured
       * height lets the stylesheet reserve exactly that much when scrolling a
       * newly revealed question into view. Without it the scroll parks the
       * question at the bottom edge — which is precisely where the pinned bar is,
       * so the thing we scrolled to was the thing it hid.
       *
       * Measured rather than guessed: the bar grows a line taller when it is
       * listing what still has to be chosen.
       */
      root.style?.setProperty?.("--uc-total-height", `${bar.offsetHeight || 0}px`);

      /*
       * A BAR THAT CANNOT PIN MUST NOT CLAIM TO BE PINNED.
       *
       * Everything below measures against the WINDOW, because on a product page
       * the window is what scrolls. Inside the quick-view drawer it is the drawer
       * body that scrolls, and the two disagree: the sentinel can be far below
       * the viewport's bottom edge while sitting in plain sight inside the
       * drawer. So the bar declared itself stuck, which is the only thing that
       * reveals its Add to cart button — and the customer got a second Add to
       * cart, in a bar that never moved, directly above the real one.
       *
       * The drawer has no use for it either way. Its buy button is a few hundred
       * pixels down the same short column, not a whole product page away, which
       * is the problem the pinned bar exists to solve.
       */
      if (root.closest?.("#uc-drawer-content")) {
        bar.classList.remove("is-stuck");
        return;
      }

      const view = win?.innerHeight || root.ownerDocument?.defaultView?.innerHeight;
      if (!view) return;
      bar.classList.toggle("is-stuck", sentinel.getBoundingClientRect().top > view);
    }

    /*
     * Throttled on the clock, not on the frame.
     *
     * requestAnimationFrame is the usual choice for a scroll-linked read and it
     * is the wrong one here: it is throttled whenever the window is occluded —
     * not merely when `document.hidden` is true — so the answer freezes at
     * whatever it was when the customer switched away and is wrong the moment
     * they come back. One rect read every 100ms costs nothing and is always
     * right; a sticky bar does not need sixty answers a second.
     *
     * Leading and trailing, so a scroll that stops between ticks still settles on
     * the correct final state.
     */
    function scheduleStuck() {
      if (Date.now() - stuckCheckedAt >= STUCK_INTERVAL) return updateStuck();
      if (stuckTimer === null) stuckTimer = setTimeout(updateStuck, STUCK_INTERVAL);
    }

    function paint() {
      const state = summary();
      root.innerHTML = renderer().render(resolution) + renderer().total(state);
      updateStuck();
      portalDrawers();
      /*
       * The theme's own scroll-lock class, so the two never fight over the body.
       *
       * KEYED ON A PICKER THAT IS ACTUALLY OPEN, not on our intention to open
       * one. Locking from `openSlot` alone means any failure to draw the picker
       * leaves the page unscrollable with nothing on screen to close — which is
       * exactly how the `uc-drawer` class collision presented on Sinnovator:
       * Change did nothing, and then the page would not move.
       *
       * The two can disagree. `drawers()` marks a picker open only when the slot
       * resolves to an option that HAS one, so an openSlot naming a question that
       * has since been hidden by another answer draws no picker at all.
       */
      const open = portalled.some((node) => node.classList?.contains("is-open"));
      doc?.documentElement?.classList.toggle("lock", open);
      onChange(state);
    }

    /** Ours, whether it is still in the mount point or has been moved to body. */
    const owns = (node) => !!node && (root.contains(node) || portalled.some((p) => p.contains(node)));

    function summary() {
      /*
       * What the customer will actually pay, which is two different things added
       * together: the customisation CHARGES, priced by the charge product for
       * each amount, and the linked PRODUCTS, priced by their own variant. Both
       * numbers come from Liquid in the local currency; neither is ever the GBP
       * figure in the rules.
       *
       * An option whose local price is unknown contributes nothing rather than a
       * converted guess — the total then understates, which is visible, instead
       * of misstating, which is not.
       */
      /*
       * ONE lookup, at the total — the same variant the cart is about to add.
       *
       * This summed the price of each option's own amount, which matches only
       * when a market's prices are proportional to the GBP ladder. EUR and USD
       * are not: £15 + £15 displayed €40 and charged €45. Reading the total's own
       * variant makes the figure on the page the figure in the cart, whatever
       * shape the price table has.
       */
      const chargeMinor = priceAt(resolution.charge.amount) ?? 0;
      const addOnMinor = resolution.addOns.reduce(
        (total, addOn) =>
          total + (addonPrices[String(addOn.variantId)] ?? 0) * (addOn.quantity || 1), 0);
      const optionsMinor = chargeMinor + addOnMinor;

      /*
       * WHAT THE OFFER JUST SAVED THEM, on the choices they have actually made.
       *
       * Only SELECTED values count. A promotion makes several things free and the
       * customer takes one of them, so totalling every discounted value on the
       * page would claim a saving on firmnesses they did not choose — the one
       * number on the page that would be flatly untrue.
       *
       * PRICED THROUGH THE LADDER, NOT SUMMED FROM GBP, for the same reason the
       * total above is: the markets are not proportional. The saving is the
       * difference between the charge this build WOULD have carried at full price
       * and the one it carries now, so it is read as two lookups rather than
       * converted — which is also why it is right when several free options are
       * taken together.
       */
      let wasAmount = 0;
      for (const option of resolution.options || []) {
        const chosen = (option.values || []).find((v) => v.value === option.selected);
        if (chosen?.wasPrice) wasAmount += chosen.wasPrice;
      }
      const savedMinor = wasAmount
        ? Math.max(0, (priceAt(resolution.charge.amount + wasAmount) ?? 0) - chargeMinor)
        : 0;

      /*
       * THE SAVING AS A PERCENTAGE — OF THE WHOLE BUILD, not of the customisation.
       *
       * Against the customisation alone it would read 100% on every Succubus,
       * where every charge is free: true, and useless. Against what the customer
       * would otherwise have paid it says the thing they care about, and it sits
       * directly under that total so it is read against it anyway.
       *
       * ROUNDED, AND NEVER SHOWN AS 0%. A saving too small to round up to one per
       * cent is still a real saving in money, and the amount beside it says so —
       * printing "(0%)" next to it would contradict the figure it is annotating.
       * Nothing is claimed rather than something false.
       */
      const beforeMinor = (basePrice + optionsMinor) + savedMinor;
      const savedPercent = savedMinor && beforeMinor
        ? Math.round((savedMinor / beforeMinor) * 100)
        : 0;

      return {
        resolution,
        selections: { ...selections },
        optionsTotalMinor: optionsMinor,
        optionsTotalLabel: formatMoney(optionsMinor),
        savedMinor,
        savedLabel: savedMinor ? formatMoney(savedMinor) : null,
        savedPercent,
        savedPercentLabel: savedPercent > 0 ? `${savedPercent}%` : null,
        basePriceMinor: basePrice,
        totalMinor: basePrice + optionsMinor,
        totalLabel: formatMoney(basePrice + optionsMinor),
        missingRequired: resolution.missingRequired,
        ready: resolution.missingRequired.length === 0,
      };
    }

    function select(name, value) {
      const option = resolution?.options?.find((o) => o.name === name);
      const chosen = option?.values?.find((v) => v.value === value);

      selections[name] = value;

      /*
       * A preset APPLIES its values into the same state the manual controls use.
       * It does not hide them — the customer sees Firmness and Colour Style
       * already answered, and can change either. The old version hid them behind
       * the button and encoded the choices in its own label, which is why PourHub
       * had to recover firmness from that label with a regex.
       */
      if (chosen?.apply) Object.assign(selections, chosen.apply);
      else syncPresets(name);

      /* Answering the question the drawer was open for closes it. Leaving it up
       * would sit a full palette over the answer the customer just gave. */
      if (openSlot === name) openSlot = null;

      const before = new Set((resolution?.options || []).map((o) => o.name));
      recompute();
      paint();
      revealNewQuestions(before);
    }

    /**
     * Bring a question the customer has just uncovered into view.
     *
     * Choosing Solid, Marble or Fade adds colour pickers BELOW the style row, and
     * on a phone they land off the bottom of the screen: the page appears to do
     * nothing, and the customer moves on without answering questions the product
     * cannot be made without.
     *
     * `block: "nearest"` scrolls the minimum needed and does nothing at all when
     * the new question is already visible, so this never yanks a desktop page
     * that had room for it anyway.
     */
    function revealNewQuestions(before) {
      const revealed = (resolution?.options || []).find((o) => !before.has(o.name));
      if (!revealed) return;
      const element = root.querySelector(`[data-uc-option="${cssEscape(revealed.name)}"]`);
      if (!element?.scrollIntoView) return;
      const still = win?.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      element.scrollIntoView({ block: "nearest", behavior: still ? "auto" : "smooth" });
    }

    /**
     * Changing something a preset set means the customer is no longer on that
     * preset, so the preset stops claiming they are. This is the whole benefit of
     * applying values rather than hiding controls: staying in step is a
     * comparison, not a mechanism.
     */
    function syncPresets(changedName) {
      for (const option of resolution?.options || []) {
        const current = option.values?.find((v) => v.value === selections[option.name]);
        if (!current?.apply || !(changedName in current.apply)) continue;
        if (current.apply[changedName] === selections[changedName]) continue;
        const plain = option.values.find((v) => !v.apply);
        if (plain) selections[option.name] = plain.value;
      }
    }

    /* ── events ──────────────────────────────────────────────────────────
     * Delegated from the root, so re-rendering never leaves stale listeners
     * and options that appear later are handled without rebinding.
     */
    function onClick(event) {
      /* Change / Choose on a colour slot opens the drawer onto that slot. */
      const opener = event.target.closest?.("[data-uc-open-drawer]");
      if (opener && owns(opener)) {
        event.preventDefault();
        openSlot = opener.getAttribute("data-uc-open-drawer");
        paint();
        return;
      }

      /* Close, the backdrop, or Escape. Closing abandons nothing: the drawer
       * only ever edits one slot and a pick applies immediately. */
      const closer = event.target.closest?.("[data-uc-close-drawer]");
      if ((closer && owns(closer)) ||
          (openSlot && event.target.classList?.contains("uc-picker"))) {
        event.preventDefault();
        openSlot = null;
        paint();
        return;
      }

      /* A hue chip filters the swatches in view. Purely visual — it changes
       * nothing about which values exist, so it never repaints and never
       * disturbs a selection. */
      const chip = event.target.closest?.("[data-uc-hue-filter]");
      if (chip && owns(chip)) {
        event.preventDefault();
        applyHueFilter(chip);
        return;
      }

      const swatch = event.target.closest?.("[data-uc-value]");
      if (!swatch || swatch.disabled || !owns(swatch)) return;
      event.preventDefault();
      /*
       * A swatch inside the drawer has no [data-uc-option] ancestor — the drawer
       * is emitted once for the whole palette, not inside the question. The slot
       * it is filling is the view state we are already holding, and falling back
       * to it is what makes one drawer serve four colour questions.
       */
      const name = swatch.closest("[data-uc-option]")?.getAttribute("data-uc-option")
        || (swatch.closest(".uc-picker") ? openSlot : null);
      if (name) select(name, swatch.getAttribute("data-uc-value"));
    }

    function onInput(event) {
      const input = event.target.closest?.("[data-uc-input]");
      if (!input || !owns(input)) return;
      const name = input.closest("[data-uc-option]")?.getAttribute("data-uc-option");
      if (!name) return;

      if (input.type === "checkbox") {
        // An unchecked box still emits its "No" value — PourHub drops those on
        // the way in, but the option must be present to be dropped.
        const off = input.getAttribute("data-uc-off");
        const on = input.getAttribute("data-uc-on");
        select(name, input.checked ? on : (off ?? on));
      } else {
        select(name, input.value);
      }
    }

    /** Arrow keys move within a radiogroup, as the role promises. */
    function onKeydown(event) {
      if (event.key === "Escape" && openSlot) {
        event.preventDefault();
        openSlot = null;
        paint();
        return;
      }
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      const current = event.target.closest?.("[data-uc-value]");
      if (!current || !owns(current)) return;

      const group = current.closest("[role=radiogroup]");
      const items = [...group.querySelectorAll("[data-uc-value]:not([disabled])")];
      const index = items.indexOf(current);
      if (index < 0) return;

      event.preventDefault();
      const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
      const next = items[(index + (forward ? 1 : -1) + items.length) % items.length];
      const name = next.closest("[data-uc-option]")?.getAttribute("data-uc-option");
      if (name) {
        select(name, next.getAttribute("data-uc-value"));
        root.querySelector(`[data-uc-option="${cssEscape(name)}"] [aria-checked="true"]`)?.focus();
      }
    }

    /** Show only the swatches in the chosen group, and mark the chip that did it. */
    function applyHueFilter(chip) {
      const wanted = chip.getAttribute("data-uc-hue-filter");
      /* Scoped to whichever container the chips are in — the drawer for a big
       * palette, the option itself for an inline pattern row. */
      const group = chip.closest(".uc-picker") || chip.closest(".uc-option");
      if (!group) return;
      for (const other of group.querySelectorAll("[data-uc-hue-filter]")) {
        other.classList.toggle("is-active", other === chip);
      }
      for (const swatch of group.querySelectorAll("[data-uc-value]")) {
        /*
         * Membership, not equality. A colour belongs to one hue but may also be a
         * glow — Glow Pink is in Pinks AND in Glow in the dark — so matching the
         * hue attribute alone could never have found it under a second chip.
         */
        const facets = (swatch.getAttribute("data-uc-facets") || "").split(" ");
        swatch.hidden = !(wanted === "all" || facets.includes(wanted));
      }
    }

    root.addEventListener("click", onClick);
    root.addEventListener("change", onInput);
    root.addEventListener("keydown", onKeydown);
    win?.addEventListener?.("scroll", scheduleStuck, { passive: true });
    win?.addEventListener?.("resize", scheduleStuck, { passive: true });

    recompute();
    paint();

    return {
      get state() { return summary(); },
      select,
      refresh({ variant: nextVariant, basePrice: nextBasePrice } = {}) {
        // The Shopify variant picker changed: conditions keyed on variant_title
        // must re-evaluate, and an option can appear or vanish as a result.
        if (nextVariant) variant = nextVariant;
        if (typeof nextBasePrice === "number") basePrice = nextBasePrice;
        recompute();
        paint();
      },
      /** The lines to POST to /cart/add.js, product first. */
      cartLines({ variantId, quantity = 1, formProperties = {} }) {
        if (resolution.missingRequired.length) {
          throw new Error(`Choose ${resolution.missingRequired.join(", ")} first`);
        }
        return cartLinesFor({
          resolution, variantId, quantity, chargeVariantFor, formProperties,
          productTitle: product?.title,
        });
      },
      destroy() {
        if (stuckTimer !== null) clearTimeout(stuckTimer);
        win?.removeEventListener?.("scroll", scheduleStuck);
        win?.removeEventListener?.("resize", scheduleStuck);
        for (const node of portalled) node.remove();
        portalled = [];
        doc?.documentElement?.classList.remove("lock");
        root.removeEventListener("click", onClick);
        root.removeEventListener("change", onInput);
        root.removeEventListener("keydown", onKeydown);
      },
    };
  }

  function cssEscape(value) {
    return String(value).replace(/["\\]/g, "\\$&");
  }

  /* ── boot.js ─────────────────────────────────────────────────────── */
  // @ts-check

  /**
   * Wires the engine into the Impact theme. The only file that knows anything
   * about Shopify or this particular theme; everything below it is portable.
   *
   * Three integration points, each with a reason it works the way it does:
   *
   *   1. ADD TO CART is intercepted rather than augmented. A configured product
   *      is several lines — the product, one charge per paid option, any add-ons
   *      — and a product form can only express one. Two sequential requests could
   *      half-succeed and leave a product with no charge line, which is an
   *      underpriced order, so all lines go in ONE POST.
   *
   *   2. REPAINT is `cart:refresh`, not `cart:change`. The drawer paints HTML
   *      captured from the /cart/add.js response, rendered BEFORE our lines
   *      existed — so cart:change alone shows a drawer missing the charge.
   *      Documented the hard way by the Auto-Gift Engine; see assets/custom.js.
   *
   *   3. VARIANT CHANGE re-resolves. 23 conditions in the live ruleset key on
   *      variant_title — "no cum tube on X-Small" and similar — so switching size
   *      can legitimately add or remove options.
   */

  const ROOT = "[data-uc-options-root]";

  /**
   * @param {object} [options]
   * @param {Document} [options.doc]
   * @param {any} [options.win]
   * @param {string} [options.routes]
   * @param {(...args:any[])=>void} [options.log]
   * @param {Document|Element} [options.scope]  where this instance's markup lives
   * @param {boolean} [options.cart]            boot the shop-wide cart work too
   */
  function boot({
    doc = document,
    win = window,
    routes = win.Shopify?.routes?.root || "/",
    log = (...args) => console.warn("[options]", ...args),
    /*
     * WHERE THIS INSTANCE'S MARKUP LIVES, and the reason there can be two.
     *
     * The quick-view drawer fetches another product's page and injects its
     * `#main` into this document — a second options root, a second add-to-cart
     * form, a second everything, all of it belonging to a different product than
     * the page around it. One instance cannot serve both: `product` is captured
     * once, and the submit interceptor has to be able to say which form is whose.
     *
     * So the drawer gets an instance of its own, scoped to the injected element.
     * Everything that used to be looked up on the document is looked up here
     * instead, which is what keeps the page's engine and the drawer's from
     * answering for each other's product.
     */
    scope = doc,
    /*
     * The cart work is shop-wide and idempotent, and the drawer is not a new
     * shop. `bootCart` guards itself, but saying so at the call site is what
     * stops a reader wondering whether opening a drawer re-reconciles the cart.
     */
    cart = true,
  } = {}) {
    /*
     * WHICH DRAWER, IF ANY, THIS INSTANCE LIVES IN. Everything below asks the
     * same question of every element it is handed: is the nearest drawer around
     * you the one around me? The page is inside no drawer, so `null` is a real
     * answer and not an absence.
     */
    const scopeDrawer = scope.closest?.("#uc-drawer-content") || null;
    const belongsHere = (node) =>
      (node?.closest?.("#uc-drawer-content") || null) === scopeDrawer;

    /*
     * THE FIRST ROOT IS NOT NECESSARILY THIS INSTANCE'S.
     *
     * A quick view injects a whole product page into this document, root and
     * all. So a document-scoped boot that took `querySelector` at its word would
     * pick up the DRAWER's root whenever the page had none of its own — which is
     * every collection page, where the engine now loads on demand precisely
     * because a drawer opened. It would render the drawer's options and then
     * decline the drawer's form, having decided it was foreign.
     */
    const root = [...scope.querySelectorAll(ROOT)].find(belongsHere) || null;
    const rules = win.UC_OPTIONS_RULES;
    /*
     * READ AT BOOT, and for the drawer that timing is the whole mechanism: the
     * injected page carries its own `<script>` setting these globals, `custom.js`
     * re-executes it, and only then is this called. So the drawer instance reads
     * the drawer product's payload from the same place the page read its own.
     *
     * It also means the globals now describe the LAST thing rendered rather than
     * the page. Nothing may read them after boot for that reason — everything
     * below closes over these constants instead.
     */
    const product = win.UC_OPTIONS_PRODUCT;

    /*
     * THE CART WORK RUNS EVERYWHERE; THE OPTIONS WORK ONLY WHERE THERE ARE OPTIONS.
     *
     * Both used to live below this early return, which meant a page with no
     * options got neither — and two things quietly depended on that being wrong.
     * The header count showed the raw item total on the cart page, on collections
     * and on the home page, so one configured dildo read as "2" everywhere except
     * the product page itself. And cart integrity only ran on product pages, so
     * removing the product from the CART page left its Customisation charge
     * behind, payable, with nothing attached to it.
     */
    if (cart) bootCart({ doc, win, routes, log });

    // Nothing more to do on a product with no options configured. Not an error.
    if (!root || !rules || !product) return null;

    /*
     * One signal for every listener this instance adds, so the drawer's can all
     * be dropped at once when it closes. Without it each quick view leaves a
     * document-level capture listener behind holding the previous product, and
     * the fifth one wins the next add to cart.
     */
    const listeners = new AbortController();
    const on = (target, type, handler, opts) =>
      target.addEventListener(type, handler, { ...(opts || {}), signal: listeners.signal });

    const chargePrices = win.UC_OPTIONS_CHARGE_PRICES || {};
    const addonPrices = win.UC_OPTIONS_ADDON_PRICES || {};
    /* Only the sold-out ids are listed; absence means available, so a snippet
     * that failed to render cannot take a whole range off sale. */
    const soldOut = win.UC_OPTIONS_ADDON_SOLD_OUT || [];
    /* Each linked product's own picture, so an option showing a product shows
     * that product rather than a URL copied out of the old app. */
    const addonImages = win.UC_OPTIONS_ADDON_IMAGES || {};
    /*
     * The colour guide's own photograph of each pour, keyed by the frozen colour
     * NAME — the only thing the browser knows a value by, the same reasoning that
     * makes `apply` translate keys to names before it ships.
     *
     * Emitted by uc-options-colour-images.liquid, which resolves the registry's
     * filenames through this store's Files. Absent, every colour falls back to
     * its hex exactly as before, so a store that has not uploaded the guide
     * photographs (Sinnovator today) loses nothing.
     */
    const colourImages = win.UC_OPTIONS_COLOUR_IMAGES || {};
    const variantPrices = product.variantPrices || {};
    if (Object.keys(chargePrices).length === 0) {
      // Options will render, priced silently. Better than a wrong price, but it
      // means the charge collection is missing — see uc-options-charge-prices.liquid.
      log("no charge prices available; options will show without prices");
    }

    const money = makeMoneyFormatter(win);
    const chargeVariantFor = win.UCOptions.makeChargeLookup({ variants: rules.charge || {} });

    const ui = win.UCOptions.mount({
      root,
      rules,
      product,
      variant: product.variant || {},
      chargePrices,
      addonPrices,
      addonImages,
      colourImages,
      soldOut,
      basePrice: product.variant?.price ?? 0,
      formatMoney: money,
      chargeVariantFor,
      onChange: (state) => {
        // Let the theme's own price block show the configured total.
        doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: state, bubbles: true }));
      },
    });

    // mount() fires onChange synchronously, before anything registered in this
    // same tick can be listening. Re-announce once the tick drains so the
    // theme's price block gets initial state without having to poll ui.state.
    setTimeout(() => {
      doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: ui.state, bubbles: true }));
    }, 0);

    /* ── Add to cart ─────────────────────────────────────────────────── */

    /*
     * Bound on the DOCUMENT in the capture phase, not on the form.
     *
     * Impact's form is a customized built-in (`<form is="product-form">`), so its
     * own submit listener is attached while the element is parsed — before this
     * deferred script runs. Listeners on the TARGET element fire in registration
     * order whatever their capture flag, so binding to the form would run second,
     * after the theme had already added the product with none of our properties.
     *
     * Capture on an ancestor genuinely precedes the target, which is the only
     * placement that lets us take over the submit.
     */
    /**
     * EVERY LINE-ITEM PROPERTY THE FORM WAS CARRYING.
     *
     * Taking over the submit means the form is never posted, so anything in it
     * that is not `id` or `quantity` was simply dropped — silently, and only on
     * products where something else put a property there.
     *
     * That cost order #40359 a ready-made's stock code and its firmness: the page
     * emitted both as hidden inputs, the engine posted its own line without them,
     * and PourHub booked a shelf item as one to manufacture.
     *
     * `form.elements` rather than `querySelectorAll`, because a field may be
     * associated with the form by its `form=` attribute while sitting outside it
     * in the DOM — which is exactly how these are rendered.
     */
    function formProperties(form, view) {
      const out = {};
      const fields = form.elements ? [...form.elements] : [...form.querySelectorAll("[name]")];
      for (const field of fields) {
        const name = field?.getAttribute?.("name") || field?.name;
        if (!name || !name.startsWith("properties[") || !name.endsWith("]")) continue;
        if (field.disabled) continue;
        // An unticked checkbox is a property the customer declined, not an empty one.
        if ((field.type === "checkbox" || field.type === "radio") && !field.checked) continue;
        const key = name.slice("properties[".length, -1);
        if (key) out[key] = field.value ?? "";
      }
      return out;
    }

    /**
     * IS THIS FORM THE ONE THIS PAGE'S OPTIONS BELONG TO?
     *
     * The listener is on the DOCUMENT in capture phase, because that is the only
     * placement that beats Impact's own product-form (see the note above). The
     * cost is that it sees EVERY add-to-cart form on the page, and a product page
     * has at least four:
     *
     *   snippets/product-info.liquid              complementary products, on the PDP itself
     *   sections/cart-drawer.liquid               cart drawer upsells
     *   sections/related-products.liquid          quick-buy on a product card
     *   sections/recently-viewed-products.liquid  the same
     *
     * Without this check every one of them was taken over as if it were the
     * configured product: the variant id came from the submitted form while the
     * options, the charge and the "For:" label came from the PDP. Seven orders
     * between 7 and 9 Sep 2026 carried a second Customisation charge for a choice
     * the customer made once. #40422 paid 11.07 "For: The Handheld Tentacle
     * Grinder II" - a product that was not in the order at all.
     *
     * TWO INDEPENDENT CHECKS, because they fail in different directions.
     *
     * The variant list is exact, but it comes from the page (uc-options.liquid
     * emits variantPrices for every variant). If it were ever missing we must NOT
     * conclude "not ours" - that hands the real product back to the theme, which
     * adds it with no charge line, and a silently underpriced order is worse than
     * a refundable double charge. An absent list abstains rather than rejects.
     *
     * The ancestry check does not depend on the page payload at all, and it
     * catches what the variant list cannot see: the SAME product quick-buyable
     * from a card, or the quick-view drawer, which injects another PDP's #main
     * into this very document.
     */
    const FOREIGN_FORM_ANCESTORS =
      ".product-card, .horizontal-product, cart-drawer, quick-buy-drawer";

    /*
     * THE DRAWER IS FOREIGN TO THE PAGE AND NATIVE TO ITSELF.
     *
     * `#uc-drawer-content` used to be in the list above, flatly. That was right
     * while the page was the only instance — the drawer held another product's
     * form and taking it over is precisely the September double-charge. It is
     * wrong now that the drawer has an instance of its own, which must own that
     * same form and nothing else.
     *
     * So the test is relative: a form is this instance's only if the nearest
     * drawer around it is the one this instance is scoped to. The page (scoped to
     * the document) is inside no drawer, so any form that is rejects — the old
     * behaviour exactly. The drawer instance accepts only forms inside itself.
     *
     * Both instances still apply the card/upsell check above, because the drawer
     * has upsells of its own.
     */
    function ownsForm(form, submittedVariantId) {
      if (form.closest?.(FOREIGN_FORM_ANCESTORS)) return false;
      if (!inScope(form)) return false;
      const known = Object.keys(product.variantPrices || {});
      if (!submittedVariantId || known.length === 0) return true;
      return known.includes(String(submittedVariantId));
    }

    /*
     * Does this element belong to THIS instance rather than the other one?
     *
     * Every listener below is still bound to the document — the submit one has
     * to be, for the capture-phase reason above, and the rest follow it so there
     * is one rule rather than two. Which means both instances see every event,
     * and each has to recognise its own. Sharing one test keeps the sticky bar,
     * the submit interceptor and the variant watcher from ever disagreeing about
     * which product a click belongs to.
     */
    const inScope = belongsHere;

    on(doc, "submit", async (event) => {
      const form = event.target;
      if (!(form instanceof win.HTMLFormElement)) return;
      if (!/\/cart\/add/.test(form.getAttribute("action") || "")) return;

      const submittedVariantId = form.querySelector('[name="id"]')?.value;

      if (ownsForm(form, submittedVariantId)) {
        event.preventDefault();
        event.stopPropagation();

        /* Everything that can start an add shows that it started one. The form's
         * own submit button is the theme's; [data-uc-add] is the sticky bar's,
         * which sits outside the form and would otherwise look inert for the
         * whole request — the customer taps it, nothing visibly happens, and
         * they tap again. Both are `is="custom-button"`, so aria-busy is all
         * either needs to run the theme's three-dot animation. */
        const busyButtons = [
          form.querySelector('[type="submit"]'),
          ...scope.querySelectorAll("[data-uc-add]"),
        ].filter(Boolean);
        const variantId = submittedVariantId || product.variant?.id;
        const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;

        let lines;
        try {
          lines = ui.cartLines({
            variantId, quantity, formProperties: formProperties(form, win),
          }).lines;
        } catch (error) {
          // A required option is unanswered. Say so and stay put rather than
          // adding a half-configured product.
          log(error.message);
          doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
            detail: { message: error.message, missing: ui.state.missingRequired }, bubbles: true,
          }));
          return;
        }

        busyButtons.forEach((el) => el.setAttribute("aria-busy", "true"));
        try {
          /*
           * WHY THIS MIRRORS IMPACT'S OWN FORM STEP FOR STEP.
           *
           * Taking over the submit meant the theme's ProductForm never ran, and
           * with it went the thing that opens the cart drawer: it is not the add
           * request that opens it but a `variant:add` event carrying a
           * server-rendered `variant-added` section. We dispatched `cart:refresh`
           * instead, which re-renders a drawer nobody opened — so the product
           * went into the cart silently and the customer had no idea whether it
           * had worked.
           *
           * `cart:prepare-bundled-sections` asks the rest of the theme which
           * sections it needs rendered with the add, rather than assuming it is
           * only ever `variant-added`; anything else listening gets its say too.
           */
          const sectionsToBundle = ["variant-added"];
          doc.documentElement?.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true, detail: { sections: sectionsToBundle },
          }));

          const response = await fetch(`${routes}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              // Without this Shopify skips the inventory check and will happily
              // add an out-of-stock linked product.
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items: lines,
              /* One request adds the product, the charge and every linked
               * product, so the section rendered from it already shows all of
               * them — which is what note 2 above was working around. */
              sections: sectionsToBundle.join(","),
              sections_url: `${routes}variants/${variantId}`,
            }),
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            log("add to cart failed:", body?.description || response.status);
            doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
              detail: { message: body?.description || "Could not add to cart" }, bubbles: true,
            }));
            return;
          }

          /* A page cart has no drawer to open. */
          if (win.themeVariables?.settings?.cartType === "page" ||
              win.themeVariables?.settings?.pageType === "cart") {
            win.location.href = `${routes}cart`;
            return;
          }

          const added = await response.json().catch(() => ({}));
          const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
          if (!cart) {
            doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
            return;
          }
          cart.sections = added.sections;

          /* From the FORM, bubbling — quick-buy drawers listen on the form they
           * belong to, while the cart drawer listens on the document. */
          form.dispatchEvent(new CustomEvent("variant:add", {
            bubbles: true,
            detail: { items: added.items || [added], cart },
          }));
          doc.documentElement?.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true, detail: { baseEvent: "variant:add", cart },
          }));
        } finally {
          busyButtons.forEach((el) => el.removeAttribute("aria-busy"));
        }
      }
    }, { capture: true });   // capture on document — see the note above

    /* The sticky bar's own button. It submits the theme's form rather than
     * posting on its own, so validation, selling plans and everything else the
     * form carries keep working — and there is exactly one add-to-cart path to
     * reason about. */
    on(doc, "click", (event) => {
      const trigger = event.target.closest?.("[data-uc-add]");
      if (!trigger || trigger.disabled) return;
      /* The page's sticky bar and the drawer's are both on screen at once while a
       * quick view is open, and both instances hear both clicks. Without this the
       * drawer's engine answered the page's button and added the drawer's
       * product alongside it. */
      if (!inScope(trigger)) return;

      /* The FIRST /cart/add form on the page is not necessarily this product's.
       * A product page carries several — the complementary-products block, cart
       * drawer upsells, quick-buy on related and recently-viewed cards — and
       * which one comes first in the DOM changes as those regions populate. That
       * is why adding a second size failed: with something already in the cart
       * the drawer had a form of its own, this picked it up, and the sticky bar
       * submitted a form belonging to a different product.
       *
       * ownsForm is the same test the submit interceptor uses, so the button and
       * the interceptor cannot disagree about which form is ours. */
      const form = Array.from(doc.querySelectorAll('form[action*="/cart/add"]'))
        .find((candidate) =>
          ownsForm(candidate, candidate.querySelector('[name="id"]')?.value));

      if (!form) return;
      event.preventDefault();
      if (typeof form.requestSubmit === "function") form.requestSubmit();
      else form.dispatchEvent(new CustomEvent("submit", { bubbles: true, cancelable: true }));
    });

    /* ── Variant changes ─────────────────────────────────────────────── */

    on(doc, "variant:change", (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;
      /* Bubbles to the document, so the page hears the drawer's size change and
       * the drawer hears the page's. Each re-resolves its own product only. */
      if (!inScope(event.target)) return;
      /* The price moves with the size, so the running total has to move with it.
       * Shopify's own event carries a price; the map is the fallback for themes
       * that dispatch only an id. */
      const basePrice = typeof variant.price === "number"
        ? variant.price
        : variantPrices[String(variant.id)];
      ui.refresh({ variant: { id: variant.id, title: variant.title }, basePrice });
    });

    /*
     * The drawer is torn down and rebuilt on every open, so its instance has to
     * be able to go away. `ui.destroy` releases the rendered UI; the controller
     * drops every listener this instance put on the document.
     */
    /* Captured BEFORE the assign below replaces it. Reading `ui.destroy` inside
     * the new one calls the new one — infinite recursion, and only on teardown,
     * which is the path nothing exercises by hand. */
    const destroyUi = ui.destroy?.bind(ui);
    return Object.assign(ui, {
      destroy() {
        listeners.abort();
        destroyUi?.();
      },
    });
  }

  /**
   * Everything about the cart that is true on every page of the shop.
   *
   * Kept apart from the options themselves because it has nothing to do with
   * them: a customer on the cart page has no options on screen and still has a
   * header count to keep honest and orphaned charge lines to be rid of.
   */
  function bootCart({ doc, win, routes, log }) {
    /*
     * Idempotent, because there are two ways in. On a product page the cart
     * bundle boots this and then the options bundle loads and boots too; on every
     * other page only the cart bundle runs. Two sets of listeners would reconcile
     * the cart twice on every change and race each other doing it.
     */
    if (win.__ucCartBooted) return;
    win.__ucCartBooted = true;

    /* ── The cart bubble counts what is being shipped ─────────────────
     *
     * A configured product is two or three cart lines, so `cart.item_count` — and
     * therefore the header bubble — said "2" for one dildo, with no way for the
     * customer to tell what the second thing was. Liquid renders the corrected
     * number (snippets/uc-cart-count.liquid), but the theme's <cart-count>
     * recomputes from the raw cart on every change and puts the wrong one back.
     *
     * Hence an observer rather than another event listener: it catches every path
     * that writes to the element, including the ones we have not gone looking
     * for, and it cannot race the theme's own async update. Writing only when the
     * value differs is what stops it chasing its own tail — the theme's setter
     * ignores an unchanged value too.
     */
    let expectedCount = null;

    const shippedCount = (cart) => (cart?.items || []).reduce(
      (total, item) => item.properties?._uc_role === "charge" ? total : total + (item.quantity || 0), 0);

    function correctCount(element) {
      if (expectedCount === null) return;
      try {
        if (element.itemCount !== expectedCount) element.itemCount = expectedCount;
      } catch {
        element.textContent = String(expectedCount);
      }
    }

    async function syncCartCount() {
      const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
      if (!cart) return;
      expectedCount = shippedCount(cart);
      for (const element of doc.querySelectorAll?.("cart-count") || []) {
        if (!element.ucCountWatched && typeof win.MutationObserver === "function") {
          element.ucCountWatched = true;
          new win.MutationObserver(() => correctCount(element))
            .observe(element, { childList: true, characterData: true, subtree: true });
        }
        correctCount(element);
      }
    }

    for (const event of ["cart:change", "cart:refresh", "variant:add"]) {
      doc.addEventListener(event, () => { syncCartCount(); });
    }
    syncCartCount();

    /* ── Cart integrity ──────────────────────────────────────────────── */

    const sync = win.UCOptions.createCartSync({
      cart: {
        get: async () => (await fetch(`${routes}cart.js`)).json(),
        add: async (items) => {
          const r = await fetch(`${routes}cart/add.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify({ items }),
          });
          return r.ok;
        },
        change: async ({ id, quantity }) => {
          const r = await fetch(`${routes}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, quantity }),
          });
          return r.ok;
        },
      },
      log,
    });

    let chain = Promise.resolve();
    const reconcile = () => {
      chain = chain.then(async () => {
        const { mutated } = await sync.reconcile();
        if (!mutated) return;
        // Same repaint split the gift engine documents: cart:refresh re-renders
        // the drawer but not the cart PAGE, which reloads instead.
        if (win.themeVariables?.settings?.pageType === "cart") reloadCartPageOnce();
        else doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }).catch((error) => log(error));
      return chain;
    };

    /*
     * The reload only repaints. By the time it is called the cart itself is
     * already correct, and checkout reads the cart, not the page — so a reload
     * that is skipped costs a stale line on screen, never money. That is why a
     * missing sessionStorage means "do not reload" rather than "reload anyway":
     * the guard is what stops a load-time reconcile reloading forever if Shopify
     * ever reports a change as applied without making it.
     */
    function reloadCartPageOnce() {
      const KEY = "uc-cart-reconciled-at";
      try {
        const last = Number(win.sessionStorage.getItem(KEY)) || 0;
        if (Date.now() - last < 10000) return;
        win.sessionStorage.setItem(KEY, String(Date.now()));
      } catch {
        return;
      }
      win.location.reload();
    }

    doc.addEventListener("cart:change", reconcile);
    win.addEventListener("pageshow", (e) => { if (e.persisted) reconcile(); });

    /*
     * AND ON EVERY PAGE LOAD, because the cart page never says it changed.
     *
     * Removing a line on the cart PAGE does not dispatch cart:change: Impact's
     * `_changeLineItemQuantity` posts /cart/change.js and then reloads the page
     * when pageType is "cart". The listener above never fires, the reloaded page
     * only listened, and the product's Customisation charge and add-ons went
     * through checkout attached to nothing. Orders #40929, #41305, #41919 and
     * #4209-SIN (Sep–Oct 2026) each paid for one: a second Werewolf charge, £22.68
     * "For: The Angel Grinder" with no grinder in the order, a Suction Cup Mount
     * charge with no mount, a second Depth Trainer III charge plus its suction cup.
     *
     * The no-JS remove links (/cart/change?id=…&quantity=0) and edits made in
     * another tab arrive the same way, as a page load with a cart already wrong.
     * Reconcile is idempotent, so on a clean cart this is one GET of /cart.js.
     */
    reconcile();

  }

  /**
   * Formats minor units in the CUSTOMER'S currency. Intl gets the symbol,
   * grouping and decimal places right per locale, which hand-rolled
   * money_format string substitution does not.
   *
   * A WHOLE AMOUNT LOSES ITS DECIMALS, because the rest of the store does the
   * same — Impact renders line prices with `money_without_trailing_zeros`, so
   * "£39.00" beside the theme's "£39" read as a different kind of number. A price
   * with real pence keeps them: £17.95 is £17.95 everywhere.
   */
  function makeMoneyFormatter(win) {
    const currency = win.UC_OPTIONS_CURRENCY || win.Shopify?.currency?.active || "GBP";
    const build = (digits) => {
      try {
        return new Intl.NumberFormat(win.Shopify?.locale || undefined, {
          style: "currency", currency,
          minimumFractionDigits: digits, maximumFractionDigits: digits,
        });
      } catch {
        return null;
      }
    };

    const withPence = build(2);
    const whole = build(0);
    if (!withPence) return (minor) => `${(minor / 100).toFixed(2)}`;

    return (minor) => (minor % 100 === 0 && whole
      ? whole.format(minor / 100)
      : withPence.format(minor / 100));
  }

  window.UCOptions = { resolve, cartLinesFor, setsForProduct, evaluateGroup, makeChargeLookup, missingChargeVariants, newToken, createRenderer, createCartSync, mount, boot, bootCart, controlFor, yesNoValues };

  // Boot once the DOM is there. Deferred script, so this usually fires
  // immediately; the readyState check covers being loaded any other way.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }
})();
